import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../lib/fetchAllRows';

interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface OpenAIConfig {
  apiKey: string;
  model: string;
  enabled: boolean;
}

/**
 * Get OpenAI configuration from system settings
 */
async function getOpenAIConfig(): Promise<OpenAIConfig> {
  try {
    // enabled + model come from the status-only edge function (non-secret).
    // The API key stays server-side in the chat-proxy function; it is never
    // needed in the browser, so apiKey is always empty here.
    const { data, error } = await supabase.functions.invoke('integration-status');
    if (error) throw error;

    return {
      apiKey: '',
      model: data?.chatgptModel || 'gpt-4o-mini',
      enabled: !!data?.status?.chatgpt?.enabled,
    };
  } catch (error) {
    console.error('Error loading OpenAI config:', error);
    return {
      apiKey: '',
      model: 'gpt-4o-mini',
      enabled: false,
    };
  }
}

/**
 * Get detailed fleet data context for AI assistant
 */
async function getFleetContext(): Promise<string> {
  try {
    // Get comprehensive analytics data
    const now = new Date();
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const [
      trucksResult, 
      driversResult, 
      loadsResult, 
      maintenanceResult, 
      recentLoads, 
      activeTrucks,
      revenueRecords,
      activeDispatches,
      completedLoads
    ] = await Promise.all([
      supabase.from('trucks').select('*', { count: 'exact', head: true }),
      supabase.from('drivers').select('*', { count: 'exact', head: true }),
      supabase.from('loads').select('*', { count: 'exact', head: true }),
      supabase.from('maintenance_logs').select('*', { count: 'exact', head: true }),
      supabase.from('loads').select('*', { count: 'exact', head: true }).in('status', ['dispatched', 'in_transit', 'en_route']),
      supabase.from('trucks').select('*', { count: 'exact', head: true }).eq('status', 'active'),
      supabase.from('revenue_records').select('total_revenue, total_miles, driver_id, truck_id, completed_date').gte('completed_date', thisMonth.toISOString()),
      supabase.from('dispatches').select('id, status, load_id, loads(total_rate)').eq('status', 'active'),
      supabase.from('revenue_records').select('*').gte('completed_date', thisMonth.toISOString())
    ]);

    // Calculate revenue metrics
    const totalRevenue = revenueRecords.data?.reduce((sum, r) => sum + Number(r.total_revenue), 0) || 0;
    const totalMiles = revenueRecords.data?.reduce((sum, r) => sum + (r.total_miles || 0), 0) || 0;
    const avgRevenuePerLoad = completedLoads.data?.length ? totalRevenue / completedLoads.data.length : 0;
    const revenuePerMile = totalMiles > 0 ? totalRevenue / totalMiles : 0;

    // Calculate driver performance
    const driverRevenue = new Map<string, number>();
    revenueRecords.data?.forEach(r => {
      if (r.driver_id) {
        driverRevenue.set(r.driver_id, (driverRevenue.get(r.driver_id) || 0) + Number(r.total_revenue));
      }
    });

    // Calculate truck performance
    const truckRevenue = new Map<string, number>();
    revenueRecords.data?.forEach(r => {
      if (r.truck_id) {
        truckRevenue.set(r.truck_id, (truckRevenue.get(r.truck_id) || 0) + Number(r.total_revenue));
      }
    });

    const context = `
You are an AI assistant for a Transportation Management System (TMS) with FULL DATABASE ACCESS and advanced analytics capabilities.

🚨 CRITICAL RULES - NEVER BREAK THESE:
1. NEVER MAKE UP DATA - If you don't have real data from the database, say "I don't have that information" or "No data found"
2. NEVER invent driver names, phone numbers, or any other details
3. NEVER use example data like "John Smith", "555-0123", "Michael Johnson", etc.
4. If the database query returns no results, say so clearly - DO NOT fabricate information
5. ONLY provide information that comes directly from database queries
6. NEVER say "Please hold on", "I need to query the database", or "Let me check" - JUST PROVIDE THE ANSWER IMMEDIATELY
7. NEVER say "I don't have the ability to execute queries" - YOU DO HAVE THIS ABILITY

⚠️ CRITICAL: You HAVE FULL DATABASE ACCESS and CAN execute queries RIGHT NOW.
When users ask about drivers, loads, trucks, or analytics, you MUST:
1. Execute the database query IMMEDIATELY (you have this capability built-in)
2. Provide the ACTUAL DATA from the query result
3. NEVER say you "need to query" or "hold on" - just give the answer directly

YOU ARE NOT A CHATBOT THAT SUGGESTS QUERIES - YOU ARE A DATABASE-CONNECTED AI THAT EXECUTES QUERIES AND PROVIDES REAL DATA.

EXAMPLES OF WHAT TO DO:
User: "How many miles did Fred Exantus drive this week?"
You: "Fred Exantus drove 942 miles this week across 2 loads, generating $3,776 in revenue."

User: "Show me dispatcher Mark's earnings this week"
You: "Dispatcher Mark handled 2 loads this week with total revenue of $3,776, averaging $1,888 per load over 942 miles."

NEVER RESPOND LIKE THIS:
❌ "I need to query the database to find out..."
❌ "Please hold on while I check..."
❌ "Let me look that up for you..."
❌ "I'll need to access the database..."

ALWAYS RESPOND LIKE THIS:
✅ Direct answer with actual numbers from database
✅ Specific metrics and data points
✅ Clear, concise information

CURRENT FLEET STATUS:
- Total Trucks: ${trucksResult.count || 0}
- Active Trucks: ${activeTrucks.count || 0}
- Total Drivers: ${driversResult.count || 0}
- Total Loads: ${loadsResult.count || 0}
- Active Loads: ${recentLoads.count || 0}
- Total Maintenance Records: ${maintenanceResult.count || 0}

REVENUE ANALYTICS (This Month):
- Total Revenue: $${(totalRevenue / 1000).toFixed(1)}K
- Completed Loads: ${completedLoads.data?.length || 0}
- Total Miles: ${totalMiles.toLocaleString()}
- Average Revenue per Load: $${avgRevenuePerLoad.toFixed(0)}
- Revenue per Mile: $${revenuePerMile.toFixed(2)}
- Active Dispatches: ${activeDispatches.count || 0}

PERFORMANCE METRICS:
- Drivers with Revenue: ${driverRevenue.size}
- Trucks with Revenue: ${truckRevenue.size}
- Average Miles per Load: ${completedLoads.data?.length ? (totalMiles / completedLoads.data.length).toFixed(0) : 0}

🗄️ COMPLETE DATABASE SCHEMA - YOU HAVE ACCESS TO EVERYTHING:

📦 LOADS & DISPATCHES (Core Operations):
TABLE: loads
- Columns: id, load_number, customer_id, driver_id, truck_id, trailer_id, status, total_rate, total_miles, pickup_location, delivery_location, pickup_city, pickup_state, delivery_city, delivery_state, pickup_date, delivery_date, created_at, created_by, user_id
- Status values: 'dispatched', 'in_transit', 'delivered', 'archived', 'cancelled'
- CRITICAL: Include status='delivered' AND status='archived' for completed loads
- Links: customer_id→customers, driver_id→drivers, truck_id→trucks, created_by→profiles

TABLE: dispatches  
- Columns: id, load_id, driver_id, truck_id, status, sms_thread_id, created_at
- Links to loads, drivers, trucks

TABLE: revenue_records
- Columns: id, load_id, load_number, customer_id, driver_id, truck_id, total_revenue, driver_pay, total_miles, pickup_city, pickup_state, delivery_city, delivery_state, completed_date
- CRITICAL: This is for loads with completed revenue - combine with loads table for full data
- Links: load_id→loads, customer_id→customers, driver_id→drivers, truck_id→trucks

🚛 FLEET MANAGEMENT (Trucks, Drivers, Equipment):
TABLE: trucks
- Columns: id, unit_number, make, model, year, vin, status, odometer, company_id, trailers, license_plate, fuel_type
- CRITICAL: Use 'unit_number' NOT 'truck_number', use 'odometer' NOT 'mileage'
- Filter: company_id IS NOT NULL for fleet trucks
- Links: company_id→companies

TABLE: drivers
- Columns: id, first_name, last_name, phone_number, email, driver_type, driver_status, dispatcher_id, company_id, is_active, hire_date
- driver_type: 'company_driver', 'owner_operator', 'rent', 'buy'
- driver_status: 'active', 'inactive', 'vacation', 'on_leave', 'terminated'
- CRITICAL: To exclude terminated drivers, use WHERE driver_status != 'terminated' OR WHERE driver_status = 'active'
- CRITICAL: "Active drivers" means driver_status = 'active', NOT is_active = true
- CRITICAL: Many drivers have company_id = NULL, so to get Apollo drivers use: WHERE (company_id = (SELECT id FROM companies WHERE LOWER(name) LIKE '%apollo%') OR company_id IS NULL)
- Links: dispatcher_id→profiles, company_id→companies

EXAMPLE QUERIES FOR DRIVERS:
Q: "How many drivers in Apollo?"
A: SELECT COUNT(*) FROM drivers WHERE company_id = (SELECT id FROM companies WHERE LOWER(name) LIKE '%apollo%') OR company_id IS NULL;

Q: "How many drivers in Apollo without terminated?"
A: SELECT COUNT(*) FROM drivers WHERE (company_id = (SELECT id FROM companies WHERE LOWER(name) LIKE '%apollo%') OR company_id IS NULL) AND driver_status != 'terminated';

Q: "How many active Apollo drivers?"
A: SELECT COUNT(*) FROM drivers WHERE (company_id = (SELECT id FROM companies WHERE LOWER(name) LIKE '%apollo%') OR company_id IS NULL) AND driver_status = 'active';

Q: "Show me drivers on vacation"
A: SELECT first_name, last_name, phone_number FROM drivers WHERE driver_status = 'vacation';

Q: "Which drivers need CDL renewal soon?"
A: SELECT first_name, last_name, cdl_expiration FROM drivers WHERE cdl_expiration < CURRENT_DATE + INTERVAL '30 days' AND driver_status = 'active' ORDER BY cdl_expiration;

EXAMPLE QUERIES FOR REVENUE & PERFORMANCE:
Q: "What's our revenue this week?"
A: SELECT SUM(total_revenue) FROM revenue_records WHERE completed_date >= date_trunc('week', CURRENT_DATE);

Q: "Top 5 drivers by revenue this month"
A: SELECT d.first_name, d.last_name, SUM(r.total_revenue) as revenue, COUNT(r.id) as loads FROM revenue_records r JOIN drivers d ON r.driver_id = d.id WHERE r.completed_date >= date_trunc('month', CURRENT_DATE) GROUP BY d.id, d.first_name, d.last_name ORDER BY revenue DESC LIMIT 5;

Q: "Which trucks made the most money this month?"
A: SELECT t.unit_number, SUM(r.total_revenue) as revenue FROM revenue_records r JOIN trucks t ON r.truck_id = t.id WHERE r.completed_date >= date_trunc('month', CURRENT_DATE) GROUP BY t.id, t.unit_number ORDER BY revenue DESC LIMIT 10;

EXAMPLE QUERIES FOR OPERATIONS:
Q: "How many loads are in transit?"
A: SELECT COUNT(*) FROM loads WHERE status IN ('dispatched', 'in_transit', 'en_route');

Q: "Show me loads delivered today"
A: SELECT load_number, customer_id, driver_id, total_rate FROM loads WHERE status = 'delivered' AND delivery_date = CURRENT_DATE;

Q: "Which trucks are idle?" or "Show me available trucks"
A: SELECT unit_number, make, model FROM trucks WHERE status = 'active' AND id NOT IN (SELECT DISTINCT truck_id FROM loads WHERE status IN ('dispatched', 'in_transit', 'en_route') AND truck_id IS NOT NULL);

EXAMPLE QUERIES FOR MAINTENANCE:
Q: "Maintenance costs this month"
A: SELECT SUM(cost) as total, SUM(company_cost_amount) as company_paid, SUM(driver_payment_amount) as driver_paid FROM maintenance_logs WHERE service_date >= date_trunc('month', CURRENT_DATE);

Q: "Which trucks need maintenance?"
A: SELECT t.unit_number, t.odometer, MAX(m.service_date) as last_service FROM trucks t LEFT JOIN maintenance_logs m ON t.id = m.truck_id WHERE t.status = 'active' GROUP BY t.id, t.unit_number, t.odometer HAVING MAX(m.service_date) < CURRENT_DATE - INTERVAL '90 days' OR MAX(m.service_date) IS NULL;

EXAMPLE QUERIES FOR CUSTOMERS:
Q: "Top customers by revenue"
A: SELECT c.name, SUM(r.total_revenue) as revenue, COUNT(r.id) as loads FROM revenue_records r JOIN customers c ON r.customer_id = c.id WHERE r.completed_date >= date_trunc('month', CURRENT_DATE) GROUP BY c.id, c.name ORDER BY revenue DESC LIMIT 10;

Q: "Show me unpaid invoices"
A: SELECT invoice_number, customer_id, amount, due_date FROM invoices WHERE status != 'paid' ORDER BY due_date;

ADVANCED ANALYTICS QUERIES:
Q: "Revenue per mile this month"
A: SELECT SUM(total_revenue) / NULLIF(SUM(total_miles), 0) as rpm FROM revenue_records WHERE completed_date >= date_trunc('month', CURRENT_DATE);

Q: "Driver utilization rate"
A: SELECT COUNT(DISTINCT driver_id) * 100.0 / (SELECT COUNT(*) FROM drivers WHERE driver_status = 'active') as utilization_percentage FROM loads WHERE status IN ('dispatched', 'in_transit', 'en_route');

Q: "Compare this month vs last month revenue"
A: SELECT 
  (SELECT SUM(total_revenue) FROM revenue_records WHERE completed_date >= date_trunc('month', CURRENT_DATE)) as this_month,
  (SELECT SUM(total_revenue) FROM revenue_records WHERE completed_date >= date_trunc('month', CURRENT_DATE - INTERVAL '1 month') AND completed_date < date_trunc('month', CURRENT_DATE)) as last_month;

TABLE: equipment_assignments
- Columns: id, driver_id, truck_id, trailer_id, assigned_at, released_at, is_current
- Use is_current=true for active assignments
- Links: driver_id→drivers, truck_id→trucks, trailer_id→trailers

TABLE: trailers
- Columns: id, unit_number, make, model, year, vin, status, trailer_type

TABLE: companies
- Columns: id, name, code (Apollo, T Mark, Tesla)
- Links to trucks, drivers via company_id

🔧 MAINTENANCE & SAFETY (Costs, Inspections, Accidents):
TABLE: maintenance_logs
- Columns: id, truck_id, trailer_id, log_number, maintenance_type, cost, labor_cost, parts_cost, company_cost_amount, service_date, service_provider, description, odometer
- CRITICAL: cost = total, company_cost_amount = company portion, driver cost = cost - company_cost_amount
- maintenance_type: 'Preventive Maintenance', 'Oil Change', 'Tire Service', 'Brake Service', 'Engine Repair', etc.
- Links: truck_id→trucks, trailer_id→trailers

TABLE: inspections
- Columns: id, driver_id, truck_id, date, type, passed, defects_found, notes
- Links: driver_id→drivers, truck_id→trucks

TABLE: accidents
- Columns: id, driver_id, truck_id, date, location, description, damage_amount, injuries, police_report_number
- Links: driver_id→drivers, truck_id→trucks

💰 ACCOUNTING & REVENUE (Money, Invoices, Expenses):
TABLE: invoices
- Columns: id, customer_id, invoice_number, amount, due_date, paid_date, status
- Links: customer_id→customers

TABLE: expenses
- Columns: id, expense_type, amount, date, truck_id, driver_id, description, receipt_url
- Links: truck_id→trucks, driver_id→drivers

TABLE: fuel_cards
- Columns: id, card_number, driver_id, truck_id, transaction_date, amount, gallons, location
- Links: driver_id→drivers, truck_id→trucks

TABLE: settlements
- Columns: id, driver_id, settlement_date, total_revenue, total_pay, deductions, net_pay
- Links: driver_id→drivers

👥 CONTACTS & CUSTOMERS (Business Relationships):
TABLE: customers
- Columns: id, name, contact_name, email, phone, address, city, state, zip, credit_limit, payment_terms
- Links from loads, revenue_records via customer_id

TABLE: brokers
- Columns: id, name, mc_number, contact_name, email, phone

TABLE: vendors
- Columns: id, name, vendor_type, contact_name, email, phone

TABLE: address_book
- Columns: id, name, company, email, phone, address, city, state, zip, category

📄 DOCUMENTS (Files, Certifications, Records):
TABLE: documents
- Columns: id, document_name, document_type, category, file_url, file_size, uploaded_at, uploaded_by
- category: 'BOL', 'POD', 'Invoice', 'Rate Confirmation', 'Insurance', etc.

TABLE: driver_documents
- Columns: id, driver_id, document_type, expiration_date, file_url, uploaded_at
- document_type: 'License', 'Medical Card', 'Hazmat', 'TWIC', etc.
- Links: driver_id→drivers

TABLE: truck_documents
- Columns: id, truck_id, document_type, expiration_date, file_url, uploaded_at
- document_type: 'Registration', 'Insurance', 'Inspection', 'Permit', etc.
- Links: truck_id→trucks

📍 GPS & TRACKING (Real-Time Location via Samsara):
TABLE: vehicle_locations
- Columns: id, vehicle_id, samsara_vehicle_id, latitude, longitude, speed, heading, timestamp
- Real-time GPS data from Samsara integration

TABLE: vehicle_diagnostics
- Columns: id, vehicle_id, fuel_percent, def_percent, battery_voltage, engine_hours, odometer, timestamp

TABLE: vehicle_fault_codes
- Columns: id, vehicle_id, spn_code, fmi_code, description, severity, timestamp, resolved

👤 USERS & SYSTEM (Access Control, Settings):
TABLE: profiles
- Columns: id, email, full_name, role, company_id, is_active, last_sign_in_at
- role: 'Superadmin', 'Admin', 'Dispatch Manager', 'Dispatcher', 'Safety', 'Accounting'

TABLE: activity_log
- Columns: id, user_id, action, entity_type, entity_id, details, created_at
- Audit trail of all system actions

TABLE: system_settings
- Columns: id, setting_key, setting_value, description
- Keys: 'samsara_api_key', 'chatgpt_api_key', 'ghl_api_key', etc.

🎯 INTELLIGENT QUERY ROUTING - HOW TO ANSWER ANY QUESTION:

STEP 1: IDENTIFY WHAT USER IS ASKING ABOUT
- Driver performance? → Query drivers + revenue_records + loads (status='delivered' OR 'archived')
- Truck metrics? → Query trucks + revenue_records + loads + maintenance_logs
- Revenue/earnings? → Query revenue_records + loads (status='delivered' OR 'archived')
- Maintenance costs? → Query maintenance_logs + trucks
- Customer analytics? → Query customers + revenue_records + loads
- Route performance? → Query loads + revenue_records (group by pickup/delivery locations)
- Dispatcher performance? → Query loads (created_by) + revenue_records
- Location/GPS? → Query vehicle_locations + trucks
- Documents/compliance? → Query driver_documents + truck_documents + expiration dates

STEP 2: DETERMINE TIME RANGE
- "this week" → last 7 days from today
- "this month" → last 30 days from today  
- "this year" → current year (Jan 1 to today)
- "last month" → previous month
- No time specified → default to last 30 days

STEP 3: COMBINE DATA SOURCES
CRITICAL: For revenue/load queries, ALWAYS combine:
1. revenue_records (completed loads with revenue)
2. loads WHERE status IN ('delivered', 'archived')
This ensures you get ALL completed work, not just loads with revenue records.

STEP 4: AGGREGATE AND CALCULATE
- SUM for totals (revenue, miles, costs)
- COUNT for quantities (loads, drivers, trucks)
- AVG for averages (revenue per load, miles per load)
- GROUP BY for breakdowns (by driver, by truck, by customer)

STEP 5: FORMAT RESPONSE
- Include specific numbers with proper formatting ($, commas)
- Show comparisons when relevant
- Provide context (timeframe, totals, averages)
- Be concise but complete

🎯 YOU CAN ANSWER ANY QUESTION ABOUT:

📊 REVENUE & FINANCIAL QUERIES:
- "What's our total revenue this week/month/year?"
- "Show me revenue by customer/driver/truck/route"
- "What's our revenue per mile?"
- "Compare this month vs last month revenue"
- "Which loads were most profitable?"
- "What's our gross profit margin?"
- "Show me unpaid invoices"
- "Total fuel costs this month"
- "Driver settlement amounts"

👤 DRIVER QUERIES:
- "Top 5/10/20 drivers by revenue"
- "How many loads did [driver name] complete?"
- "What are [driver name]'s earnings this week/month?"
- "Which driver has the most miles?"
- "Show me driver efficiency metrics"
- "Who are my company drivers vs owner operators?"
- "Which drivers need document renewals?"
- "Driver accident history"
- "Driver inspection pass rates"
- "Where is driver [name] right now?"

🚛 TRUCK QUERIES:
- "Which trucks are most profitable?"
- "Show me truck [number] performance"
- "Truck [number] maintenance costs"
- "Which trucks have highest mileage?"
- "Trucks by company (Apollo/T Mark/Tesla)"
- "Which trucks are idle/available?"
- "Truck [number] current location"
- "Trucks needing maintenance"
- "Truck utilization rates"
- "Fuel consumption by truck"

👥 CUSTOMER QUERIES:
- "Top customers by revenue"
- "How many loads for customer [name]?"
- "Average rate per customer"
- "Customer payment history"
- "Which customers owe money?"
- "Customer load frequency"
- "Best paying customers"

🗺️ ROUTE & LOCATION QUERIES:
- "Most profitable routes"
- "Revenue by state/city"
- "Routes with highest rates"
- "Average miles per route"
- "Pickup/delivery location analysis"
- "Which states generate most revenue?"

📋 DISPATCHER QUERIES:
- "Dispatcher [name] performance"
- "Loads created by each dispatcher"
- "Which dispatcher handles most loads?"
- "Revenue by dispatcher"

🔧 MAINTENANCE QUERIES:
- "Total maintenance costs this week/month/year"
- "Truck [number] maintenance history"
- "Parts vs labor breakdown"
- "Most expensive maintenance types"
- "Trucks with most maintenance issues"
- "Upcoming maintenance due"
- "Maintenance costs by company"
- "Driver vs company maintenance costs"

📄 COMPLIANCE & DOCUMENTS:
- "Which drivers have expiring licenses?"
- "Show me document expiration alerts"
- "Truck [number] insurance status"
- "Medical card expirations"
- "Missing documents report"

📍 GPS & TRACKING:
- "Where is truck [number]?"
- "Show me all vehicle locations"
- "Which trucks are moving?"
- "Vehicle diagnostics for truck [number]"
- "Fuel levels across fleet"
- "Active fault codes"

⚙️ OPERATIONAL QUERIES:
- "How many active loads?"
- "Completed loads this week"
- "Load status breakdown"
- "On-time delivery rate"
- "Average delivery time"
- "Loads by status"
- "Current dispatch assignments"

💡 COMPARISON QUERIES:
- "This week vs last week revenue"
- "This month vs last month loads"
- "Driver A vs Driver B performance"
- "Truck A vs Truck B profitability"
- "Year over year growth"

🔍 SPECIFIC LOOKUPS:
- "Load number [12345] details"
- "Customer [name] contact info"
- "Driver [name] phone number"
- "Truck [number] VIN"
- "Invoice [number] status"

🎯 QUERY EXECUTION INSTRUCTIONS:

FOR REVENUE QUESTIONS:
1. Query revenue_records for loads with completed revenue
2. Query loads WHERE status IN ('delivered', 'archived') for all completed loads
3. Combine both sources (SUM revenue from both)
4. Join with drivers/trucks/customers as needed
5. Apply date filters
6. Calculate totals, averages, per-mile rates

FOR DRIVER PERFORMANCE:
1. Query drivers table for driver info
2. Query revenue_records + loads (delivered/archived) for their loads
3. Calculate: total revenue, load count, total miles, avg per load
4. Include maintenance costs if truck-specific
5. Sort by revenue/loads/miles as requested

FOR TRUCK PERFORMANCE:
1. Query trucks table for truck info
2. Query revenue_records + loads for truck's loads
3. Query maintenance_logs for costs
4. Calculate: revenue, loads, miles, maintenance cost, net profit
5. Include utilization metrics

FOR MAINTENANCE:
1. Query maintenance_logs with date filter
2. Join with trucks table for truck details
3. Calculate: total cost, parts vs labor, by type
4. Group by truck/type/date as needed

FOR LOCATION/GPS:
1. Query vehicle_locations for real-time position
2. Join with trucks table for truck details
3. Include speed, heading, timestamp
4. Check vehicle_diagnostics for fuel/DEF levels

ALWAYS:
- Apply date filters based on user's timeframe
- Combine revenue_records + loads (delivered/archived) for complete data
- Format currency with $ and commas
- Include specific numbers, not vague estimates
- Show totals, averages, and counts
- Provide context (timeframe, comparisons)

RESPONSE FORMAT - CRITICAL:
- NEVER use markdown formatting (NO **, *, _, #, ##, etc.)
- Write in plain text only
- Use simple dashes (-) for bullet points
- Be concise and conversational
- Include specific numbers and percentages
- Keep responses short (2-4 sentences max)
- NO pleasantries like "feel free to ask", "if you need more details", "let me know"
- Just answer the question directly and stop
`;

    return context;
  } catch (error) {
    console.error('Error getting fleet context:', error);
    return 'Fleet data temporarily unavailable. Please try again.';
  }
}

/**
 * Query all trucks with their basic info
 */
async function queryAllTrucks(): Promise<string> {
  try {
    // Query trucks with company_id (fleet trucks only)
    const { data, error } = await supabase
      .from('trucks')
      .select('id, unit_number, make, model, year, status, odometer, company:companies(name, code)')
      .not('company_id', 'is', null)
      .order('unit_number')
      .limit(50);

    if (error) throw error;

    if (!data || data.length === 0) {
      return 'No fleet trucks found in the system. Note: Only trucks with assigned companies are shown.';
    }

    const summary = `Found ${data.length} fleet trucks:\n\n` +
      data.map((truck, idx) => {
        const company = (truck as any).company;
        return `${idx + 1}. Truck ${truck.unit_number} - ${truck.year} ${truck.make} ${truck.model}\n` +
        `   Company: ${company?.code || 'N/A'}, Status: ${truck.status}, Odometer: ${truck.odometer?.toLocaleString() || 'N/A'} miles`;
      }).join('\n\n');

    return summary;
  } catch (error) {
    console.error('Error querying trucks:', error);
    return 'Error retrieving truck data.';
  }
}

/**
 * Query trucks with most maintenance issues
 */
async function queryTrucksWithMostIssues(limit: number = 10): Promise<string> {
  try {
    const { data, error } = await supabase
      .from('maintenance_logs')
      .select(`
        truck_id,
        trucks (truck_number, make, model)
      `)
      .order('service_date', { ascending: false });

    if (error) throw error;

    if (!data || data.length === 0) {
      return 'No maintenance records found.';
    }

    // Count issues per truck
    const truckCounts = data.reduce((acc: any, log: any) => {
      const truckId = log.truck_id;
      if (!acc[truckId]) {
        const truckInfo = Array.isArray(log.trucks) ? log.trucks[0] : log.trucks;
        acc[truckId] = {
          count: 0,
          truck_number: truckInfo?.truck_number || 'Unknown',
          make: truckInfo?.make || '',
          model: truckInfo?.model || '',
        };
      }
      acc[truckId].count++;
      return acc;
    }, {});

    const sorted = Object.values(truckCounts)
      .sort((a: any, b: any) => b.count - a.count)
      .slice(0, limit);

    const summary = `Trucks with most maintenance records (Top ${Math.min(limit, sorted.length)}):\n\n` +
      sorted.map((truck: any, idx) => 
        `${idx + 1}. ${truck.truck_number} ${truck.make} ${truck.model}\n` +
        `   Maintenance Records: ${truck.count}`
      ).join('\n\n');

    return summary;
  } catch (error) {
    console.error('Error querying trucks with issues:', error);
    return 'Error retrieving maintenance data.';
  }
}

/**
 * Query driver load statistics
 */
async function queryDriverLoadStats(timeframe: 'week' | 'month' = 'month'): Promise<string> {
  try {
    const daysAgo = timeframe === 'week' ? 7 : 30;
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysAgo);

    const data = await fetchAllRows<any>(() => supabase
      .from('loads')
      .select(`
        id,
        driver_id,
        drivers (first_name, last_name)
      `)
      .gte('pickup_date', startDate.toISOString())
      .not('driver_id', 'is', null));

    if (!data || data.length === 0) {
      return `No loads found in the last ${timeframe}.`;
    }

    // Count loads per driver
    const driverCounts = data.reduce((acc: any, load: any) => {
      const driverId = load.driver_id;
      if (!acc[driverId]) {
        const driverInfo = Array.isArray(load.drivers) ? load.drivers[0] : load.drivers;
        acc[driverId] = {
          count: 0,
          name: driverInfo ? `${driverInfo.first_name} ${driverInfo.last_name}` : 'Unknown',
        };
      }
      acc[driverId].count++;
      return acc;
    }, {});

    const sorted = Object.values(driverCounts)
      .sort((a: any, b: any) => b.count - a.count)
      .slice(0, 10);

    const summary = `Driver load statistics (Last ${timeframe}):\n\n` +
      sorted.map((driver: any, idx) => 
        `${idx + 1}. ${driver.name}: ${driver.count} loads`
      ).join('\n');

    return summary;
  } catch (error) {
    console.error('Error querying driver stats:', error);
    return 'Error retrieving driver statistics.';
  }
}

/**
 * Query total maintenance costs across fleet
 */
async function queryFleetMaintenanceCosts(): Promise<string> {
  try {
    const { data, error, count } = await supabase
      .from('maintenance_logs')
      .select(`
        id,
        maintenance_type,
        cost,
        labor_cost,
        parts_cost,
        company_cost_amount,
        service_date
      `, { count: 'exact' });

    console.log('Maintenance query result:', { data, error, count });

    if (error) {
      console.error('Supabase error:', error);
      return `Error querying maintenance data: ${error.message}. Please check database permissions.`;
    }

    if (!data || data.length === 0) {
      return 'No maintenance records found in the system. You may need to add maintenance records first, or check if you have permission to view them.';
    }

    // Calculate totals - use company_cost_amount as primary cost source
    const totalRecords = data.length;
    const totalCost = data.reduce((sum, log) => {
      const cost = Number(log.cost) || Number(log.company_cost_amount) || 0;
      return sum + cost;
    }, 0);
    const totalLabor = data.reduce((sum, log) => sum + (Number(log.labor_cost) || 0), 0);
    const totalParts = data.reduce((sum, log) => sum + (Number(log.parts_cost) || 0), 0);
    const totalCompanyCost = data.reduce((sum, log) => sum + (Number(log.company_cost_amount) || 0), 0);
    const totalDriverCost = totalCost - totalCompanyCost;

    // Group by maintenance type
    const costsByType = data.reduce((acc: any, log) => {
      const type = log.maintenance_type;
      const cost = Number(log.cost) || Number(log.company_cost_amount) || 0;
      if (!acc[type]) {
        acc[type] = { count: 0, total: 0 };
      }
      acc[type].count++;
      acc[type].total += cost;
      return acc;
    }, {});

    const typeBreakdown = Object.entries(costsByType)
      .sort((a: any, b: any) => b[1].total - a[1].total)
      .slice(0, 5)
      .map(([type, data]: [string, any]) => 
        `- ${type}: ${data.count} records, $${data.total.toLocaleString()}`
      )
      .join('\n');

    const summary = `FLEET MAINTENANCE COST SUMMARY:\n\n` +
      `Total Maintenance Records: ${totalRecords}\n\n` +
      `TOTAL COSTS:\n` +
      `- Total Spent: $${totalCost.toLocaleString()}\n` +
      `- Company Paid: $${totalCompanyCost.toLocaleString()}\n` +
      `- Driver Paid: $${totalDriverCost.toLocaleString()}\n` +
      `- Labor Costs: $${totalLabor.toLocaleString()}\n` +
      `- Parts Costs: $${totalParts.toLocaleString()}\n\n` +
      `TOP MAINTENANCE TYPES BY COST:\n${typeBreakdown}`;

    return summary;
  } catch (error) {
    console.error('Error querying fleet maintenance costs:', error);
    return 'Error retrieving fleet maintenance cost data.';
  }
}

/**
 * Query driver location and current assignment
 */
async function queryDriverLocation(driverName: string): Promise<string> {
  try {
    // Search for driver by name
    const { data: drivers, error: driverError } = await supabase
      .from('drivers')
      .select('id, first_name, last_name, phone')
      .or(`first_name.ilike.%${driverName}%,last_name.ilike.%${driverName}%`)
      .limit(5);

    if (driverError) throw driverError;

    if (!drivers || drivers.length === 0) {
      return `No driver found matching "${driverName}". Please check the spelling or try a different name.`;
    }

    // Get current loads for the driver(s)
    const driverIds = drivers.map(d => d.id);
    const { data: loads, error: loadError } = await supabase
      .from('loads')
      .select(`
        id,
        load_number,
        status,
        pickup_location,
        delivery_location,
        pickup_date,
        delivery_date,
        current_latitude,
        current_longitude,
        trucks (truck_number),
        drivers (first_name, last_name)
      `)
      .in('driver_id', driverIds)
      .in('status', ['assigned', 'in_transit', 'at_pickup', 'at_delivery'])
      .order('pickup_date', { ascending: false })
      .limit(1);

    if (loadError) throw loadError;

    let response = '';

    // Build response for each driver found
    for (const driver of drivers) {
      const driverFullName = `${driver.first_name} ${driver.last_name}`;
      const driverLoad = loads?.find(l => {
        const loadDriver = Array.isArray(l.drivers) ? l.drivers[0] : l.drivers;
        return loadDriver && `${loadDriver.first_name} ${loadDriver.last_name}` === driverFullName;
      });

      if (driverLoad) {
        const truck = Array.isArray(driverLoad.trucks) ? driverLoad.trucks[0] : driverLoad.trucks;
        response += `DRIVER: ${driverFullName}\n\n`;
        response += `Current Status: ${driverLoad.status}\n`;
        response += `Load Number: ${driverLoad.load_number}\n`;
        response += `Truck: ${truck?.truck_number || 'N/A'}\n\n`;

        if (driverLoad.current_latitude && driverLoad.current_longitude) {
          response += `📍 CURRENT LOCATION:\n`;
          response += `Latitude: ${driverLoad.current_latitude}\n`;
          response += `Longitude: ${driverLoad.current_longitude}\n\n`;
        }

        response += `Route:\n`;
        response += `📍 Pickup: ${driverLoad.pickup_location}\n`;
        response += `🎯 Delivery: ${driverLoad.delivery_location}\n\n`;

        if (driverLoad.pickup_date) {
          response += `Pickup Date: ${new Date(driverLoad.pickup_date).toLocaleString()}\n`;
        }
        if (driverLoad.delivery_date) {
          response += `Delivery Date: ${new Date(driverLoad.delivery_date).toLocaleString()}\n`;
        }
      } else {
        response += `DRIVER: ${driverFullName}\n`;
        response += `Status: Not currently assigned to an active load\n`;
        response += `Phone: ${driver.phone || 'N/A'}\n\n`;
      }
    }

    return response || `Found driver(s) but no active load information available.`;
  } catch (error) {
    console.error('Error querying driver location:', error);
    return 'Error retrieving driver location data.';
  }
}

/**
 * Query driver earnings/revenue for a specific time period
 */
async function queryDriverEarnings(driverName: string, timeframe: 'week' | 'month' | 'all' = 'month'): Promise<string> {
  try {
    // Search for driver by name
    const { data: drivers, error: driverError } = await supabase
      .from('drivers')
      .select('id, first_name, last_name')
      .or(`first_name.ilike.%${driverName}%,last_name.ilike.%${driverName}%`)
      .limit(5);

    if (driverError) throw driverError;

    if (!drivers || drivers.length === 0) {
      return `No driver found matching "${driverName}". Please check the spelling.`;
    }

    const driver = drivers[0];
    const driverFullName = `${driver.first_name} ${driver.last_name}`;

    // Calculate date range
    let startDate = new Date();
    if (timeframe === 'week') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (timeframe === 'month') {
      startDate.setMonth(startDate.getMonth() - 1);
    } else {
      startDate = new Date('2000-01-01'); // All time
    }

    // Get revenue records for this driver
    const { data: revenueData, error: revenueError } = await supabase
      .from('revenue_records')
      .select('total_revenue, driver_pay, total_miles, completed_date, load_number')
      .eq('driver_id', driver.id)
      .gte('completed_date', startDate.toISOString())
      .order('completed_date', { ascending: false });

    if (revenueError) throw revenueError;

    // ALSO get archived/delivered loads without revenue records
    const { data: archivedLoads, error: loadsError } = await supabase
      .from('loads')
      .select('total_rate, total_miles, created_at, load_number, status')
      .eq('driver_id', driver.id)
      .in('status', ['delivered', 'archived'])
      .gte('created_at', startDate.toISOString())
      .order('created_at', { ascending: false });

    if (loadsError) throw loadsError;

    // Combine both data sources
    const allLoads = [
      ...(revenueData || []).map(r => ({
        revenue: Number(r.total_revenue || 0),
        driverPay: Number(r.driver_pay || 0),
        miles: r.total_miles || 0,
        date: r.completed_date,
        loadNumber: r.load_number
      })),
      ...(archivedLoads || []).map(l => ({
        revenue: Number(l.total_rate || 0),
        driverPay: 0, // No driver pay data for archived loads
        miles: l.total_miles || 0,
        date: l.created_at,
        loadNumber: l.load_number
      }))
    ];

    if (allLoads.length === 0) {
      return `${driverFullName} has no completed loads in the selected timeframe (${timeframe}).`;
    }

    const totalRevenue = allLoads.reduce((sum, l) => sum + l.revenue, 0);
    const totalDriverPay = allLoads.reduce((sum, l) => sum + l.driverPay, 0);
    const totalMiles = allLoads.reduce((sum, l) => sum + l.miles, 0);
    const loadCount = allLoads.length;
    const avgPayPerLoad = loadCount > 0 ? totalDriverPay / loadCount : 0;
    const avgMilesPerLoad = loadCount > 0 ? totalMiles / loadCount : 0;

    let response = `DRIVER EARNINGS - ${driverFullName}\n`;
    response += `Timeframe: ${timeframe === 'week' ? 'Last 7 Days' : timeframe === 'month' ? 'Last 30 Days' : 'All Time'}\n\n`;
    response += `SUMMARY:\n`;
    response += `- Total Loads Completed: ${loadCount}\n`;
    response += `- Total Driver Pay: $${totalDriverPay.toFixed(2)}\n`;
    response += `- Total Revenue Generated: $${totalRevenue.toFixed(2)}\n`;
    response += `- Total Miles: ${totalMiles.toLocaleString()}\n`;
    response += `- Average Pay per Load: $${avgPayPerLoad.toFixed(2)}\n`;
    response += `- Average Miles per Load: ${avgMilesPerLoad.toFixed(0)}\n\n`;

    if (allLoads.length > 0) {
      response += `RECENT LOADS (Last 5):\n`;
      allLoads.slice(0, 5).forEach((load, idx) => {
        response += `${idx + 1}. Load ${load.loadNumber}\n`;
        response += `   Date: ${new Date(load.date).toLocaleDateString()}\n`;
        response += `   Revenue: $${load.revenue.toFixed(2)}\n`;
        response += `   Miles: ${load.miles || 0}\n\n`;
      });
    }

    return response;
  } catch (error) {
    console.error('Error querying driver earnings:', error);
    return 'Error retrieving driver earnings data.';
  }
}

/**
 * Query truck mileage for a specific time period
 */
async function queryTruckMileage(truckNumber: string, timeframe: 'week' | 'month' | 'all' = 'month'): Promise<string> {
  try {
    // Find truck by number
    const { data: truck, error: truckError } = await supabase
      .from('trucks')
      .select('id, unit_number, make, model, year, mileage')
      .ilike('unit_number', `%${truckNumber}%`)
      .limit(1)
      .single();

    if (truckError || !truck) {
      return `No truck found matching "${truckNumber}". Please check the truck number.`;
    }

    // Calculate date range
    let startDate = new Date();
    if (timeframe === 'week') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (timeframe === 'month') {
      startDate.setMonth(startDate.getMonth() - 1);
    } else {
      startDate = new Date('2000-01-01');
    }

    // Get mileage logs for this truck
    const { data: mileageLogs, error: logsError } = await supabase
      .from('truck_mileage_logs')
      .select('miles_driven, log_date, starting_mileage, ending_mileage')
      .eq('truck_id', truck.id)
      .gte('log_date', startDate.toISOString())
      .order('log_date', { ascending: false });

    if (logsError) throw logsError;

    // Also get revenue records for this truck
    const { data: revenueData, error: revenueError } = await supabase
      .from('revenue_records')
      .select('total_miles, completed_date, load_number')
      .eq('truck_id', truck.id)
      .gte('completed_date', startDate.toISOString())
      .order('completed_date', { ascending: false });

    if (revenueError) throw revenueError;

    const totalMilesFromLogs = mileageLogs?.reduce((sum, log) => sum + (log.miles_driven || 0), 0) || 0;
    const totalMilesFromRevenue = revenueData?.reduce((sum, r) => sum + (r.total_miles || 0), 0) || 0;
    const totalMiles = Math.max(totalMilesFromLogs, totalMilesFromRevenue);
    const loadCount = revenueData?.length || 0;

    let response = `TRUCK MILEAGE - ${truck.unit_number}\n`;
    response += `${truck.year} ${truck.make} ${truck.model}\n`;
    response += `Timeframe: ${timeframe === 'week' ? 'Last 7 Days' : timeframe === 'month' ? 'Last 30 Days' : 'All Time'}\n\n`;
    response += `SUMMARY:\n`;
    response += `- Current Odometer: ${truck.mileage?.toLocaleString() || 'N/A'} miles\n`;
    response += `- Miles Driven (Period): ${totalMiles.toLocaleString()} miles\n`;
    response += `- Loads Completed: ${loadCount}\n`;
    
    if (loadCount > 0) {
      response += `- Average Miles per Load: ${(totalMiles / loadCount).toFixed(0)}\n`;
    }

    if (revenueData && revenueData.length > 0) {
      response += `\nRECENT TRIPS (Last 5):\n`;
      revenueData.slice(0, 5).forEach((record, idx) => {
        response += `${idx + 1}. Load ${record.load_number}\n`;
        response += `   Date: ${new Date(record.completed_date).toLocaleDateString()}\n`;
        response += `   Miles: ${record.total_miles || 0}\n\n`;
      });
    }

    return response;
  } catch (error) {
    console.error('Error querying truck mileage:', error);
    return 'Error retrieving truck mileage data.';
  }
}

/**
 * Query weekly revenue totals
 */
async function queryWeeklyRevenue(): Promise<string> {
  try {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
    endOfWeek.setHours(23, 59, 59, 999);

    // Get revenue records for this week
    const { data: revenueData, error } = await supabase
      .from('revenue_records')
      .select('total_revenue, driver_pay, total_miles, completed_date, load_number')
      .gte('completed_date', startOfWeek.toISOString())
      .lte('completed_date', endOfWeek.toISOString())
      .order('completed_date', { ascending: false });

    if (error) throw error;

    if (!revenueData || revenueData.length === 0) {
      return `No completed loads found for this week (${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()}).`;
    }

    const totalRevenue = revenueData.reduce((sum, r) => sum + Number(r.total_revenue || 0), 0);
    const totalDriverPay = revenueData.reduce((sum, r) => sum + Number(r.driver_pay || 0), 0);
    const totalMiles = revenueData.reduce((sum, r) => sum + (r.total_miles || 0), 0);
    const loadCount = revenueData.length;
    const profit = totalRevenue - totalDriverPay;
    const revenuePerMile = totalMiles > 0 ? totalRevenue / totalMiles : 0;

    let response = `WEEKLY REVENUE REPORT\n`;
    response += `Week: ${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()}\n\n`;
    response += `SUMMARY:\n`;
    response += `- Total Revenue: $${totalRevenue.toFixed(2)}\n`;
    response += `- Total Driver Pay: $${totalDriverPay.toFixed(2)}\n`;
    response += `- Gross Profit: $${profit.toFixed(2)}\n`;
    response += `- Loads Completed: ${loadCount}\n`;
    response += `- Total Miles: ${totalMiles.toLocaleString()}\n`;
    response += `- Revenue per Mile: $${revenuePerMile.toFixed(2)}\n`;
    response += `- Average Revenue per Load: $${(totalRevenue / loadCount).toFixed(2)}\n\n`;

    // Group by day
    const byDay = revenueData.reduce((acc: any, r) => {
      const day = new Date(r.completed_date).toLocaleDateString('en-US', { weekday: 'short' });
      if (!acc[day]) {
        acc[day] = { revenue: 0, loads: 0 };
      }
      acc[day].revenue += Number(r.total_revenue || 0);
      acc[day].loads += 1;
      return acc;
    }, {});

    response += `DAILY BREAKDOWN:\n`;
    Object.entries(byDay).forEach(([day, data]: [string, any]) => {
      response += `- ${day}: ${data.loads} loads, $${data.revenue.toFixed(2)}\n`;
    });

    return response;
  } catch (error) {
    console.error('Error querying weekly revenue:', error);
    return 'Error retrieving weekly revenue data.';
  }
}

/**
 * Query maintenance costs for a specific truck with date range
 */
async function queryTruckMaintenanceCosts(truckNumber: string, timeframe: 'week' | 'month' | 'year' | 'all' = 'all'): Promise<string> {
  try {
    // Find truck
    const { data: truck, error: truckError } = await supabase
      .from('trucks')
      .select('id, unit_number, make, model, year')
      .ilike('unit_number', `%${truckNumber}%`)
      .limit(1)
      .single();

    if (truckError || !truck) {
      return `No truck found matching "${truckNumber}".`;
    }

    // Calculate date range
    let startDate = new Date();
    if (timeframe === 'week') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (timeframe === 'month') {
      startDate.setMonth(startDate.getMonth() - 1);
    } else if (timeframe === 'year') {
      startDate.setFullYear(startDate.getFullYear() - 1);
    } else {
      startDate = new Date('2000-01-01');
    }

    // Get maintenance logs
    const { data: maintenanceLogs, error } = await supabase
      .from('maintenance_logs')
      .select('maintenance_type, cost, labor_cost, parts_cost, service_date, service_provider, description')
      .eq('truck_id', truck.id)
      .gte('service_date', startDate.toISOString())
      .order('service_date', { ascending: false });

    if (error) throw error;

    if (!maintenanceLogs || maintenanceLogs.length === 0) {
      return `No maintenance records found for truck ${truck.unit_number} in the selected timeframe.`;
    }

    const totalCost = maintenanceLogs.reduce((sum, log) => sum + Number(log.cost || 0), 0);
    const totalLabor = maintenanceLogs.reduce((sum, log) => sum + Number(log.labor_cost || 0), 0);
    const totalParts = maintenanceLogs.reduce((sum, log) => sum + Number(log.parts_cost || 0), 0);

    let response = `MAINTENANCE COSTS - ${truck.unit_number}\n`;
    response += `${truck.year} ${truck.make} ${truck.model}\n`;
    response += `Timeframe: ${timeframe === 'week' ? 'Last 7 Days' : timeframe === 'month' ? 'Last 30 Days' : timeframe === 'year' ? 'Last Year' : 'All Time'}\n\n`;
    response += `TOTAL COSTS:\n`;
    response += `- Total Spent: $${totalCost.toFixed(2)}\n`;
    response += `- Labor: $${totalLabor.toFixed(2)}\n`;
    response += `- Parts: $${totalParts.toFixed(2)}\n`;
    response += `- Service Records: ${maintenanceLogs.length}\n\n`;

    response += `RECENT MAINTENANCE (Last 5):\n`;
    maintenanceLogs.slice(0, 5).forEach((log, idx) => {
      response += `${idx + 1}. ${log.maintenance_type}\n`;
      response += `   Date: ${new Date(log.service_date).toLocaleDateString()}\n`;
      response += `   Cost: $${Number(log.cost || 0).toFixed(2)}\n`;
      response += `   Provider: ${log.service_provider || 'N/A'}\n\n`;
    });

    return response;
  } catch (error) {
    console.error('Error querying truck maintenance costs:', error);
    return 'Error retrieving maintenance cost data.';
  }
}

/**
 * Query top customers by revenue
 */
async function queryTopCustomers(limit: number = 10, timeframe: 'week' | 'month' | 'all' = 'month'): Promise<string> {
  try {
    let startDate = new Date();
    if (timeframe === 'week') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (timeframe === 'month') {
      startDate.setMonth(startDate.getMonth() - 1);
    } else {
      startDate = new Date('2000-01-01');
    }

    const { data: revenueData, error } = await supabase
      .from('revenue_records')
      .select('total_revenue, customer_id, customers(name)')
      .gte('completed_date', startDate.toISOString())
      .not('customer_id', 'is', null);

    if (error) throw error;

    if (!revenueData || revenueData.length === 0) {
      return `No customer revenue data found for the selected timeframe.`;
    }

    // Aggregate by customer
    const customerRevenue = revenueData.reduce((acc: any, record: any) => {
      const customerId = record.customer_id;
      const customerName = record.customers?.name || 'Unknown';
      if (!acc[customerId]) {
        acc[customerId] = { name: customerName, revenue: 0, loads: 0 };
      }
      acc[customerId].revenue += Number(record.total_revenue || 0);
      acc[customerId].loads += 1;
      return acc;
    }, {});

    const sorted = Object.values(customerRevenue)
      .sort((a: any, b: any) => b.revenue - a.revenue)
      .slice(0, limit);

    let response = `TOP ${limit} CUSTOMERS BY REVENUE\n`;
    response += `Timeframe: ${timeframe === 'week' ? 'Last 7 Days' : timeframe === 'month' ? 'Last 30 Days' : 'All Time'}\n\n`;
    
    sorted.forEach((customer: any, idx) => {
      response += `${idx + 1}. ${customer.name}\n`;
      response += `   Revenue: $${customer.revenue.toFixed(2)}\n`;
      response += `   Loads: ${customer.loads}\n`;
      response += `   Avg per Load: $${(customer.revenue / customer.loads).toFixed(2)}\n\n`;
    });

    return response;
  } catch (error) {
    console.error('Error querying top customers:', error);
    return 'Error retrieving customer data.';
  }
}

/**
 * Query trucks by company
 */
async function queryTrucksByCompany(companyName?: string): Promise<string> {
  try {
    let query = supabase
      .from('trucks')
      .select('id, unit_number, make, model, year, status, company:companies(id, name, code)')
      .not('company_id', 'is', null);

    // If company name provided, filter by it
    if (companyName) {
      const { data: companies } = await supabase
        .from('companies')
        .select('id, name, code')
        .or(`name.ilike.%${companyName}%,code.ilike.%${companyName}%`);

      if (!companies || companies.length === 0) {
        return `No company found matching "${companyName}". Available companies: Apollo, T Mark, Tesla.`;
      }

      query = query.eq('company_id', companies[0].id);
    }

    const { data, error } = await query.order('unit_number');

    if (error) throw error;

    if (!data || data.length === 0) {
      return companyName 
        ? `No trucks found for ${companyName}.`
        : 'No fleet trucks found in the system.';
    }

    // Group by company if showing all
    if (!companyName) {
      const byCompany = data.reduce((acc: any, truck: any) => {
        const companyCode = truck.company?.code || 'Unknown';
        if (!acc[companyCode]) {
          acc[companyCode] = { name: truck.company?.name || 'Unknown', trucks: [] };
        }
        acc[companyCode].trucks.push(truck);
        return acc;
      }, {});

      let response = `FLEET TRUCKS BY COMPANY:\n\n`;
      Object.entries(byCompany).forEach(([code, info]: [string, any]) => {
        response += `${code} (${info.name}): ${info.trucks.length} trucks\n`;
      });
      response += `\nTotal: ${data.length} trucks`;
      return response;
    }

    // Show details for specific company
    const company = (data[0] as any).company;
    let response = `${company.name.toUpperCase()} FLEET:\n`;
    response += `Total Trucks: ${data.length}\n\n`;
    
    // Group by status
    const byStatus = data.reduce((acc: any, truck) => {
      const status = truck.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    response += `STATUS BREAKDOWN:\n`;
    Object.entries(byStatus).forEach(([status, count]) => {
      response += `- ${status}: ${count}\n`;
    });

    response += `\nTRUCKS:\n`;
    data.slice(0, 20).forEach((truck, idx) => {
      response += `${idx + 1}. ${truck.unit_number} - ${truck.year} ${truck.make} ${truck.model} (${truck.status})\n`;
    });

    if (data.length > 20) {
      response += `\n... and ${data.length - 20} more trucks`;
    }

    return response;
  } catch (error) {
    console.error('Error querying trucks by company:', error);
    return 'Error retrieving truck data by company.';
  }
}

/**
 * Query which driver is assigned to a truck with full details
 */
async function queryTruckDriver(truckNumber: string): Promise<string> {
  try {
    // Find truck by unit number (include dispatcher_id from truck)
    const { data: truck, error: truckError } = await supabase
      .from('trucks')
      .select('id, unit_number, make, model, year, trailers, dispatcher_id')
      .ilike('unit_number', `%${truckNumber}%`)
      .limit(1)
      .single();

    if (truckError || !truck) {
      return `No truck found matching "${truckNumber}".`;
    }

    // Find current driver assignment
    const { data: assignment, error: assignmentError } = await supabase
      .from('equipment_assignments')
      .select(`
        driver_id, 
        trailer_id,
        drivers(first_name, last_name, phone_number, driver_type),
        trailers(unit_number, make, model)
      `)
      .eq('truck_id', truck.id)
      .eq('is_current', true)
      .limit(1)
      .single();

    if (assignmentError || !assignment || !assignment.drivers) {
      return `Truck ${truck.unit_number} (${truck.year} ${truck.make} ${truck.model}) is not currently assigned to any driver.`;
    }

    const driver = assignment.drivers as any;
    
    // Get dispatcher info from truck (not driver)
    let dispatcherName = 'N/A';
    if (truck.dispatcher_id) {
      const { data: dispatcher } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', truck.dispatcher_id)
        .single();
      
      if (dispatcher) {
        dispatcherName = dispatcher.full_name || dispatcher.email?.split('@')[0] || 'N/A';
      }
    }

    // Build response
    let response = `TRUCK ${truck.unit_number} ASSIGNMENT:\n\n`;
    response += `Driver: ${driver.first_name} ${driver.last_name}\n`;
    response += `Driver Type: ${driver.driver_type || 'N/A'}\n`;
    response += `Dispatcher: ${dispatcherName}\n`;
    
    if (driver.phone_number) {
      response += `Phone: ${driver.phone_number}\n`;
    }

    // Trailer info from assignment
    const assignedTrailer = assignment.trailers as any;
    if (assignedTrailer) {
      response += `\nTrailer: ${assignedTrailer.unit_number}`;
      if (assignedTrailer.make || assignedTrailer.model) {
        response += ` (${assignedTrailer.make} ${assignedTrailer.model})`;
      }
    } else if (truck.trailers) {
      // Fallback to truck's trailers field
      response += `\nTrailer: ${truck.trailers}`;
    } else {
      response += `\nTrailer: Not assigned`;
    }

    return response;
  } catch (error) {
    console.error('Error querying truck driver:', error);
    return 'Error retrieving truck driver assignment.';
  }
}

/**
 * Query how many trucks a dispatcher has
 */
async function queryDispatcherTrucks(dispatcherName: string): Promise<string> {
  try {
    // Find dispatcher by name
    const { data: dispatcher, error: dispatcherError } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .or(`full_name.ilike.%${dispatcherName}%,email.ilike.%${dispatcherName}%`)
      .limit(1)
      .single();

    if (dispatcherError || !dispatcher) {
      return `No dispatcher found matching "${dispatcherName}".`;
    }

    // Count trucks assigned to this dispatcher (all statuses)
    const { data: trucks, error: trucksError } = await supabase
      .from('trucks')
      .select('id, unit_number, make, model, year, status')
      .eq('dispatcher_id', dispatcher.id);

    if (trucksError) {
      console.error('Error querying dispatcher trucks:', trucksError);
      return 'Error retrieving truck data.';
    }

    const truckCount = trucks?.length || 0;
    const dispatcherDisplayName = dispatcher.full_name || dispatcher.email?.split('@')[0] || dispatcherName;

    if (truckCount === 0) {
      return `${dispatcherDisplayName} has no trucks assigned.`;
    }

    let response = `${dispatcherDisplayName} has ${truckCount} truck${truckCount > 1 ? 's' : ''} assigned:\n\n`;
    
    trucks?.forEach((truck, idx) => {
      response += `${idx + 1}. Truck ${truck.unit_number} - ${truck.year} ${truck.make} ${truck.model}\n`;
    });

    return response;
  } catch (error) {
    console.error('Error querying dispatcher trucks:', error);
    return 'Error retrieving dispatcher truck information.';
  }
}

/**
 * Query trucks with their drivers for a dispatcher
 */
async function queryDispatcherTrucksWithDrivers(dispatcherName: string): Promise<string> {
  try {
    // Find dispatcher by name
    const { data: dispatcher, error: dispatcherError } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .or(`full_name.ilike.%${dispatcherName}%,email.ilike.%${dispatcherName}%`)
      .limit(1)
      .single();

    if (dispatcherError || !dispatcher) {
      return `No dispatcher found matching "${dispatcherName}".`;
    }

    // Get trucks assigned to this dispatcher
    const { data: trucks, error: trucksError } = await supabase
      .from('trucks')
      .select('id, unit_number, make, model, year')
      .eq('dispatcher_id', dispatcher.id)
      .order('unit_number');

    if (trucksError) {
      console.error('Error querying dispatcher trucks:', trucksError);
      return 'Error retrieving truck data.';
    }

    if (!trucks || trucks.length === 0) {
      return `${dispatcher.full_name || dispatcherName} has no trucks assigned.`;
    }

    // Get current driver assignments for these trucks
    const truckIds = trucks.map(t => t.id);
    const { data: assignments } = await supabase
      .from('equipment_assignments')
      .select('truck_id, driver_id, drivers(first_name, last_name, phone_number)')
      .in('truck_id', truckIds)
      .eq('is_current', true);

    // Create a map of truck_id to driver
    const driverMap = new Map();
    assignments?.forEach((assignment: any) => {
      if (assignment.drivers) {
        driverMap.set(assignment.truck_id, assignment.drivers);
      }
    });

    const dispatcherDisplayName = dispatcher.full_name || dispatcher.email?.split('@')[0] || dispatcherName;
    let response = `${dispatcherDisplayName}'s trucks and drivers:\n\n`;
    
    trucks.forEach((truck, idx) => {
      const driver = driverMap.get(truck.id);
      response += `${idx + 1}. Truck ${truck.unit_number} - ${truck.year} ${truck.make}`;
      if (driver) {
        response += `\n   Driver: ${driver.first_name} ${driver.last_name}`;
        if (driver.phone_number) {
          response += ` - ${driver.phone_number}`;
        }
      } else {
        response += `\n   Driver: Unassigned`;
      }
      response += '\n\n';
    });

    return response;
  } catch (error) {
    console.error('Error querying dispatcher trucks with drivers:', error);
    return 'Error retrieving truck and driver information.';
  }
}

/**
 * Query drivers assigned to a dispatcher
 */
async function queryDispatcherDrivers(dispatcherName: string): Promise<string> {
  try {
    // Find dispatcher by name
    const { data: dispatcher, error: dispatcherError } = await supabase
      .from('profiles')
      .select('id, full_name, email')
      .or(`full_name.ilike.%${dispatcherName}%,email.ilike.%${dispatcherName}%`)
      .limit(1)
      .single();

    if (dispatcherError || !dispatcher) {
      return `No dispatcher found matching "${dispatcherName}".`;
    }

    // Get drivers assigned to this dispatcher
    const { data: drivers, error: driversError } = await supabase
      .from('drivers')
      .select('id, first_name, last_name, phone_number, driver_type, driver_status')
      .eq('dispatcher_id', dispatcher.id)
      .order('last_name');

    if (driversError) {
      console.error('Error querying dispatcher drivers:', driversError);
      return 'Error retrieving driver data.';
    }

    const driverCount = drivers?.length || 0;
    const dispatcherDisplayName = dispatcher.full_name || dispatcher.email?.split('@')[0] || dispatcherName;

    if (driverCount === 0) {
      return `${dispatcherDisplayName} has no drivers assigned.`;
    }

    let response = `${dispatcherDisplayName} has ${driverCount} driver${driverCount > 1 ? 's' : ''} assigned:\n\n`;
    
    drivers?.forEach((driver, idx) => {
      response += `${idx + 1}. ${driver.first_name} ${driver.last_name}`;
      if (driver.driver_type) {
        const typeLabel = driver.driver_type === 'owner_operator' ? 'Owner Operator' : 
                         driver.driver_type === 'company_driver' ? 'Company' : driver.driver_type;
        response += ` (${typeLabel})`;
      }
      if (driver.phone_number) {
        response += ` - ${driver.phone_number}`;
      }
      response += '\n';
    });

    return response;
  } catch (error) {
    console.error('Error querying dispatcher drivers:', error);
    return 'Error retrieving dispatcher driver information.';
  }
}

/**
 * Query route/lane analytics
 */
async function queryRouteAnalytics(timeframe: 'week' | 'month' | 'all' = 'month'): Promise<string> {
  try {
    let startDate = new Date();
    if (timeframe === 'week') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (timeframe === 'month') {
      startDate.setMonth(startDate.getMonth() - 1);
    } else {
      startDate = new Date('2000-01-01');
    }

    const loads = await fetchAllRows<any>(() => supabase
      .from('loads')
      .select('id, pickup_city, pickup_state, delivery_city, delivery_state, total_rate, total_miles')
      .gte('pickup_date', startDate.toISOString())
      .not('pickup_state', 'is', null)
      .not('delivery_state', 'is', null));

    if (!loads || loads.length === 0) {
      return `No route data found for the selected timeframe.`;
    }

    // Aggregate by route
    const routeStats = loads.reduce((acc: any, load: any) => {
      const route = `${load.pickup_state} → ${load.delivery_state}`;
      if (!acc[route]) {
        acc[route] = { revenue: 0, loads: 0, miles: 0 };
      }
      acc[route].revenue += Number(load.total_rate || 0);
      acc[route].loads += 1;
      acc[route].miles += load.total_miles || 0;
      return acc;
    }, {});

    const sorted = Object.entries(routeStats)
      .map(([route, stats]: [string, any]) => ({
        route,
        ...stats,
        avgRate: stats.loads > 0 ? stats.revenue / stats.loads : 0,
        ratePerMile: stats.miles > 0 ? stats.revenue / stats.miles : 0
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);

    let response = `TOP 10 ROUTES BY REVENUE\n`;
    response += `Timeframe: ${timeframe === 'week' ? 'Last 7 Days' : timeframe === 'month' ? 'Last 30 Days' : 'All Time'}\n\n`;
    
    sorted.forEach((route, idx) => {
      response += `${idx + 1}. ${route.route}\n`;
      response += `   Revenue: $${route.revenue.toFixed(2)}\n`;
      response += `   Loads: ${route.loads}\n`;
      response += `   Miles: ${route.miles.toLocaleString()}\n`;
      response += `   Rate/Mile: $${route.ratePerMile.toFixed(2)}\n\n`;
    });

    return response;
  } catch (error) {
    console.error('Error querying route analytics:', error);
    return 'Error retrieving route data.';
  }
}

/**
 * Query truck utilization and idle trucks
 */
async function queryTruckUtilization(timeframe: 'week' | 'month' = 'month'): Promise<string> {
  try {
    const daysAgo = timeframe === 'week' ? 7 : 30;
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysAgo);

    // Get all fleet trucks (company_id IS NOT NULL means it's a fleet truck)
    const { data: trucks, error: trucksError } = await supabase
      .from('trucks')
      .select('id, unit_number, status, company:companies(name, code)')
      .not('company_id', 'is', null)
      .order('unit_number');

    if (trucksError) throw trucksError;

    if (!trucks || trucks.length === 0) {
      return 'No fleet trucks found in the system.';
    }

    // Get loads for each truck - include both active and completed loads
    const loads = await fetchAllRows<any>(() => supabase
      .from('loads')
      .select('id, truck_id, status, pickup_date, delivery_date, total_rate, total_miles, created_at')
      .gte('created_at', startDate.toISOString())
      .not('truck_id', 'is', null));

    // Also get revenue records for completed loads
    const { data: revenueRecords, error: revenueError } = await supabase
      .from('revenue_records')
      .select('truck_id, total_revenue, total_miles, completed_date')
      .gte('completed_date', startDate.toISOString())
      .not('truck_id', 'is', null);

    if (revenueError) throw revenueError;

    // Calculate utilization per truck - combine loads and revenue records
    const truckStats = trucks.map(truck => {
      const company = (truck as any).company;
      
      // Get loads for this truck
      const truckLoads = loads?.filter(l => l.truck_id === truck.id) || [];
      
      // Get revenue records for this truck
      const truckRevenue = revenueRecords?.filter(r => r.truck_id === truck.id) || [];
      
      // Combine both sources
      const totalLoads = truckLoads.length + truckRevenue.length;
      const revenueFromLoads = truckLoads.reduce((sum, l) => sum + Number(l.total_rate || 0), 0);
      const revenueFromRecords = truckRevenue.reduce((sum, r) => sum + Number(r.total_revenue || 0), 0);
      const totalRevenue = revenueFromLoads + revenueFromRecords;
      
      const milesFromLoads = truckLoads.reduce((sum, l) => sum + (l.total_miles || 0), 0);
      const milesFromRecords = truckRevenue.reduce((sum, r) => sum + (r.total_miles || 0), 0);
      const totalMiles = milesFromLoads + milesFromRecords;
      
      return {
        unit_number: truck.unit_number,
        company: company?.code || 'N/A',
        loads: totalLoads,
        revenue: totalRevenue,
        miles: totalMiles,
        utilization: totalLoads > 0 ? ((totalLoads / daysAgo) * 100).toFixed(1) : '0.0'
      };
    });

    const sorted = truckStats.sort((a, b) => b.revenue - a.revenue);
    const idle = sorted.filter(t => t.loads === 0);

    let response = `TRUCK UTILIZATION REPORT\n`;
    response += `Timeframe: Last ${daysAgo} Days\n\n`;
    response += `Total Fleet Trucks: ${trucks.length}\n`;
    response += `Idle Trucks: ${idle.length}\n`;
    response += `Active Trucks: ${trucks.length - idle.length}\n\n`;

    if (idle.length > 0) {
      response += `⚠️ IDLE TRUCKS (No Loads in Last ${daysAgo} Days):\n`;
      idle.forEach(truck => {
        response += `- Truck ${truck.unit_number} (${truck.company})\n`;
      });
      response += `\n`;
    } else {
      response += `✅ All trucks are active - no idle trucks!\n\n`;
    }

    response += `TOP 10 MOST UTILIZED TRUCKS:\n`;
    sorted.slice(0, 10).forEach((truck, idx) => {
      response += `${idx + 1}. ${truck.unit_number}\n`;
      response += `   Loads: ${truck.loads}\n`;
      response += `   Revenue: $${truck.revenue.toFixed(2)}\n`;
      response += `   Miles: ${truck.miles.toLocaleString()}\n`;
      response += `   Utilization: ${truck.utilization}%\n\n`;
    });

    return response;
  } catch (error) {
    console.error('Error querying truck utilization:', error);
    return 'Error retrieving truck utilization data.';
  }
}

/**
 * Query driver performance metrics
 */
async function queryDriverPerformance(timeframe: 'week' | 'month' = 'month'): Promise<string> {
  try {
    const daysAgo = timeframe === 'week' ? 7 : 30;
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysAgo);

    const { data: revenueData, error } = await supabase
      .from('revenue_records')
      .select('driver_id, total_revenue, driver_pay, total_miles, completed_date, drivers(first_name, last_name)')
      .gte('completed_date', startDate.toISOString())
      .not('driver_id', 'is', null);

    if (error) throw error;

    if (!revenueData || revenueData.length === 0) {
      return `No driver performance data found for the selected timeframe.`;
    }

    // Aggregate by driver
    const driverStats = revenueData.reduce((acc: any, record: any) => {
      const driverId = record.driver_id;
      const driverName = record.drivers ? `${record.drivers.first_name} ${record.drivers.last_name}` : 'Unknown';
      
      if (!acc[driverId]) {
        acc[driverId] = { name: driverName, revenue: 0, pay: 0, miles: 0, loads: 0 };
      }
      acc[driverId].revenue += Number(record.total_revenue || 0);
      acc[driverId].pay += Number(record.driver_pay || 0);
      acc[driverId].miles += record.total_miles || 0;
      acc[driverId].loads += 1;
      return acc;
    }, {});

    const sorted = Object.values(driverStats)
      .map((driver: any) => ({
        ...driver,
        revenuePerMile: driver.miles > 0 ? driver.revenue / driver.miles : 0,
        avgPayPerLoad: driver.loads > 0 ? driver.pay / driver.loads : 0
      }))
      .sort((a: any, b: any) => b.revenue - a.revenue)
      .slice(0, 10);

    let response = `TOP 10 DRIVER PERFORMANCE\n`;
    response += `Timeframe: Last ${daysAgo} Days\n\n`;
    
    sorted.forEach((driver: any, idx) => {
      response += `${idx + 1}. ${driver.name}\n`;
      response += `   Loads: ${driver.loads}\n`;
      response += `   Revenue: $${driver.revenue.toFixed(2)}\n`;
      response += `   Driver Pay: $${driver.pay.toFixed(2)}\n`;
      response += `   Miles: ${driver.miles.toLocaleString()}\n`;
      response += `   Revenue/Mile: $${driver.revenuePerMile.toFixed(2)}\n\n`;
    });

    return response;
  } catch (error) {
    console.error('Error querying driver performance:', error);
    return 'Error retrieving driver performance data.';
  }
}

/**
 * Query predictive maintenance - trucks due for service
 */
async function queryPredictiveMaintenance(): Promise<string> {
  try {
    const { data: trucks, error } = await supabase
      .from('trucks')
      .select(`
        id,
        unit_number,
        make,
        model,
        mileage,
        maintenance_logs(service_date, odometer, maintenance_type)
      `)
      .eq('status', 'active')
      .order('mileage', { ascending: false });

    if (error) throw error;

    if (!trucks || trucks.length === 0) {
      return 'No active trucks found.';
    }

    const maintenanceIntervals = {
      'Oil Change': 15000,
      'Tire Service': 50000,
      'Brake Service': 100000,
      'DOT Inspection': 12 // months
    };

    const dueForService: any[] = [];

    trucks.forEach((truck: any) => {
      const currentMileage = truck.mileage || 0;
      const logs = Array.isArray(truck.maintenance_logs) ? truck.maintenance_logs : [];
      
      // Check oil change
      const lastOilChange = logs
        .filter((l: any) => l.maintenance_type === 'Oil Change')
        .sort((a: any, b: any) => new Date(b.service_date).getTime() - new Date(a.service_date).getTime())[0];
      
      if (!lastOilChange || (currentMileage - (lastOilChange.odometer || 0)) > maintenanceIntervals['Oil Change']) {
        dueForService.push({
          truck: truck.unit_number,
          service: 'Oil Change',
          milesSince: lastOilChange ? currentMileage - (lastOilChange.odometer || 0) : currentMileage,
          priority: 'High'
        });
      }
    });

    if (dueForService.length === 0) {
      return 'All trucks are up to date with maintenance! ✅';
    }

    let response = `⚠️ PREDICTIVE MAINTENANCE ALERTS\n\n`;
    response += `${dueForService.length} truck(s) need service:\n\n`;
    
    dueForService.forEach((item, idx) => {
      response += `${idx + 1}. ${item.truck} - ${item.service}\n`;
      response += `   Miles Since Last Service: ${item.milesSince.toLocaleString()}\n`;
      response += `   Priority: ${item.priority}\n\n`;
    });

    return response;
  } catch (error) {
    console.error('Error querying predictive maintenance:', error);
    return 'Error retrieving maintenance predictions.';
  }
}

/**
 * Query real-time operations dashboard
 */
async function queryRealTimeOperations(): Promise<string> {
  try {
    const [trucksResult, driversResult, loadsResult, activeLoads] = await Promise.all([
      supabase.from('trucks').select('*', { count: 'exact', head: true }).eq('status', 'active'),
      supabase.from('drivers').select('*', { count: 'exact', head: true }),
      supabase.from('loads').select('*', { count: 'exact', head: true }),
      supabase.from('loads').select('id, load_number, status, pickup_location, delivery_location, drivers(first_name, last_name), trucks(unit_number)')
        .in('status', ['dispatched', 'in_transit', 'at_pickup', 'at_delivery'])
        .limit(10)
    ]);

    const activeTrucks = trucksResult.count || 0;
    const totalDrivers = driversResult.count || 0;
    const totalLoads = loadsResult.count || 0;
    const activeLoadsList = activeLoads.data || [];

    let response = `🚛 REAL-TIME OPERATIONS DASHBOARD\n\n`;
    response += `FLEET STATUS:\n`;
    response += `• Active Trucks: ${activeTrucks}\n`;
    response += `• Total Drivers: ${totalDrivers}\n`;
    response += `• Total Loads: ${totalLoads}\n`;
    response += `• Active Loads: ${activeLoadsList.length}\n\n`;

    if (activeLoadsList.length > 0) {
      response += `ACTIVE LOADS RIGHT NOW:\n`;
      activeLoadsList.forEach((load: any, idx) => {
        const driver = load.drivers ? `${load.drivers.first_name} ${load.drivers.last_name}` : 'Unassigned';
        const truck = load.trucks?.unit_number || 'N/A';
        response += `${idx + 1}. Load ${load.load_number}\n`;
        response += `   Status: ${load.status}\n`;
        response += `   Driver: ${driver}\n`;
        response += `   Truck: ${truck}\n`;
        response += `   Route: ${load.pickup_location} → ${load.delivery_location}\n\n`;
      });
    }

    return response;
  } catch (error) {
    console.error('Error querying real-time operations:', error);
    return 'Error retrieving operations data.';
  }
}

/**
 * Query financial deep dive - profit margins
 */
async function queryFinancialAnalysis(timeframe: 'week' | 'month' = 'month'): Promise<string> {
  try {
    const daysAgo = timeframe === 'week' ? 7 : 30;
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysAgo);

    const { data: revenueData, error: revenueError } = await supabase
      .from('revenue_records')
      .select('total_revenue, driver_pay, total_miles, completed_date')
      .gte('completed_date', startDate.toISOString());

    if (revenueError) throw revenueError;

    const { data: maintenanceData, error: maintenanceError } = await supabase
      .from('maintenance_logs')
      .select('cost, service_date')
      .gte('service_date', startDate.toISOString());

    if (maintenanceError) throw maintenanceError;

    const totalRevenue = revenueData?.reduce((sum, r) => sum + Number(r.total_revenue || 0), 0) || 0;
    const totalDriverPay = revenueData?.reduce((sum, r) => sum + Number(r.driver_pay || 0), 0) || 0;
    const totalMiles = revenueData?.reduce((sum, r) => sum + (r.total_miles || 0), 0) || 0;
    const totalMaintenance = maintenanceData?.reduce((sum, m) => sum + Number(m.cost || 0), 0) || 0;
    
    const grossProfit = totalRevenue - totalDriverPay;
    const netProfit = grossProfit - totalMaintenance;
    const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;
    const costPerMile = totalMiles > 0 ? (totalDriverPay + totalMaintenance) / totalMiles : 0;
    const revenuePerMile = totalMiles > 0 ? totalRevenue / totalMiles : 0;

    let response = `💰 FINANCIAL ANALYSIS\n`;
    response += `Timeframe: Last ${daysAgo} Days\n\n`;
    response += `REVENUE:\n`;
    response += `• Total Revenue: $${totalRevenue.toFixed(2)}\n`;
    response += `• Revenue per Mile: $${revenuePerMile.toFixed(2)}\n\n`;
    response += `COSTS:\n`;
    response += `• Driver Pay: $${totalDriverPay.toFixed(2)}\n`;
    response += `• Maintenance: $${totalMaintenance.toFixed(2)}\n`;
    response += `• Cost per Mile: $${costPerMile.toFixed(2)}\n\n`;
    response += `PROFITABILITY:\n`;
    response += `• Gross Profit: $${grossProfit.toFixed(2)}\n`;
    response += `• Net Profit: $${netProfit.toFixed(2)}\n`;
    response += `• Profit Margin: ${profitMargin.toFixed(1)}%\n`;

    return response;
  } catch (error) {
    console.error('Error querying financial analysis:', error);
    return 'Error retrieving financial data.';
  }
}

/**
 * Query compliance and document expiration
 */
async function queryComplianceAlerts(): Promise<string> {
  try {
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    const { data: driverDocs, error: driverError } = await supabase
      .from('driver_documents')
      .select('document_type, expiration_date, drivers(first_name, last_name)')
      .not('expiration_date', 'is', null)
      .lte('expiration_date', thirtyDaysFromNow.toISOString())
      .order('expiration_date');

    if (driverError) throw driverError;

    const { data: truckDocs, error: truckError } = await supabase
      .from('truck_documents')
      .select('document_type, expiration_date, trucks(unit_number)')
      .not('expiration_date', 'is', null)
      .lte('expiration_date', thirtyDaysFromNow.toISOString())
      .order('expiration_date');

    if (truckError) throw truckError;

    const expired: any[] = [];
    const expiringSoon: any[] = [];

    driverDocs?.forEach((doc: any) => {
      const expDate = new Date(doc.expiration_date);
      const item = {
        type: 'Driver',
        name: doc.drivers ? `${doc.drivers.first_name} ${doc.drivers.last_name}` : 'Unknown',
        document: doc.document_type,
        expiration: expDate.toLocaleDateString()
      };
      
      if (expDate < today) {
        expired.push(item);
      } else {
        expiringSoon.push(item);
      }
    });

    truckDocs?.forEach((doc: any) => {
      const expDate = new Date(doc.expiration_date);
      const item = {
        type: 'Truck',
        name: doc.trucks?.unit_number || 'Unknown',
        document: doc.document_type,
        expiration: expDate.toLocaleDateString()
      };
      
      if (expDate < today) {
        expired.push(item);
      } else {
        expiringSoon.push(item);
      }
    });

    let response = `⚠️ COMPLIANCE ALERTS\n\n`;

    if (expired.length > 0) {
      response += `🔴 EXPIRED DOCUMENTS (${expired.length}):\n`;
      expired.forEach((item, idx) => {
        response += `${idx + 1}. ${item.type}: ${item.name}\n`;
        response += `   Document: ${item.document}\n`;
        response += `   Expired: ${item.expiration}\n\n`;
      });
    }

    if (expiringSoon.length > 0) {
      response += `🟡 EXPIRING SOON (Next 30 Days):\n`;
      expiringSoon.forEach((item, idx) => {
        response += `${idx + 1}. ${item.type}: ${item.name}\n`;
        response += `   Document: ${item.document}\n`;
        response += `   Expires: ${item.expiration}\n\n`;
      });
    }

    if (expired.length === 0 && expiringSoon.length === 0) {
      response += `✅ All documents are up to date!`;
    }

    return response;
  } catch (error) {
    console.error('Error querying compliance alerts:', error);
    return 'Error retrieving compliance data.';
  }
}

/**
 * Query detailed driver information by ID or name
 */
async function queryDriverDetails(driverIdentifier: string): Promise<string> {
  try {
    // Try to find driver by ID first, then by name
    let driver;
    
    // Check if it's a UUID
    if (driverIdentifier.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      const { data } = await supabase
        .from('drivers')
        .select('*, trucks(unit_number), trailers(unit_number), dispatchers:profiles!drivers_dispatcher_id_fkey(full_name)')
        .eq('id', driverIdentifier)
        .single();
      driver = data;
    } else {
      // Search by name
      const { data } = await supabase
        .from('drivers')
        .select('*, trucks(unit_number), trailers(unit_number), dispatchers:profiles!drivers_dispatcher_id_fkey(full_name)')
        .or(`first_name.ilike.%${driverIdentifier}%,last_name.ilike.%${driverIdentifier}%`)
        .limit(1)
        .single();
      driver = data;
    }

    if (!driver) {
      return `Driver "${driverIdentifier}" not found.`;
    }

    // Get recent loads
    const { data: recentLoads } = await supabase
      .from('loads')
      .select('load_number, status, pickup_location, delivery_location, pickup_date, total_rate')
      .eq('driver_id', driver.id)
      .order('pickup_date', { ascending: false })
      .limit(5);

    // Get revenue stats
    const { data: revenueStats } = await supabase
      .from('revenue_records')
      .select('total_revenue, driver_pay, total_miles')
      .eq('driver_id', driver.id)
      .gte('completed_date', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

    const totalRevenue = revenueStats?.reduce((sum, r) => sum + Number(r.total_revenue || 0), 0) || 0;
    const totalPay = revenueStats?.reduce((sum, r) => sum + Number(r.driver_pay || 0), 0) || 0;
    const totalMiles = revenueStats?.reduce((sum, r) => sum + (r.total_miles || 0), 0) || 0;

    let response = `👨‍✈️ DRIVER DETAILS: ${driver.first_name} ${driver.last_name}\n\n`;
    response += `CONTACT INFO:\n`;
    response += `• Phone: ${driver.phone || 'N/A'}\n`;
    response += `• Email: ${driver.email || 'N/A'}\n\n`;
    response += `ASSIGNMENT:\n`;
    response += `• Dispatcher: ${driver.dispatchers?.full_name || 'Unassigned'}\n`;
    response += `• Truck: ${driver.trucks?.unit_number || 'Unassigned'}\n`;
    response += `• Trailer: ${driver.trailers?.unit_number || 'Unassigned'}\n`;
    response += `• Type: ${driver.driver_type === 'owner_operator' ? 'Owner Operator' : 'Company Driver'}\n\n`;
    response += `PERFORMANCE (Last 30 Days):\n`;
    response += `• Loads: ${revenueStats?.length || 0}\n`;
    response += `• Revenue Generated: $${totalRevenue.toFixed(2)}\n`;
    response += `• Driver Pay: $${totalPay.toFixed(2)}\n`;
    response += `• Miles: ${totalMiles.toLocaleString()}\n\n`;

    if (recentLoads && recentLoads.length > 0) {
      response += `RECENT LOADS:\n`;
      recentLoads.forEach((load, idx) => {
        response += `${idx + 1}. ${load.load_number} - ${load.status}\n`;
        response += `   ${load.pickup_location} → ${load.delivery_location}\n`;
        response += `   Rate: $${load.total_rate || 0}\n\n`;
      });
    }

    return response;
  } catch (error) {
    console.error('Error querying driver details:', error);
    return 'Error retrieving driver information.';
  }
}

/**
 * Query detailed load information by load number or ID
 */
async function queryLoadDetails(loadIdentifier: string): Promise<string> {
  try {
    let load;
    
    // Check if it's a UUID
    if (loadIdentifier.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      const { data } = await supabase
        .from('loads')
        .select(`
          *,
          drivers(first_name, last_name, phone),
          trucks(unit_number),
          trailers(unit_number),
          customers(name),
          brokers(name)
        `)
        .eq('id', loadIdentifier)
        .single();
      load = data;
    } else {
      // Search by load number
      const { data } = await supabase
        .from('loads')
        .select(`
          *,
          drivers(first_name, last_name, phone),
          trucks(unit_number),
          trailers(unit_number),
          customers(name),
          brokers(name)
        `)
        .ilike('load_number', `%${loadIdentifier}%`)
        .limit(1)
        .single();
      load = data;
    }

    if (!load) {
      return `Load "${loadIdentifier}" not found.`;
    }

    // Get load stops
    const { data: stops } = await supabase
      .from('load_stops')
      .select('*')
      .eq('load_id', load.id)
      .order('stop_order');

    let response = `📦 LOAD DETAILS: ${load.load_number}\n\n`;
    response += `STATUS: ${load.status}\n\n`;
    response += `ROUTE:\n`;
    response += `• Pickup: ${load.pickup_location}\n`;
    response += `• Pickup Date: ${load.pickup_date ? new Date(load.pickup_date).toLocaleString() : 'N/A'}\n`;
    response += `• Delivery: ${load.delivery_location}\n`;
    response += `• Delivery Date: ${load.delivery_date ? new Date(load.delivery_date).toLocaleString() : 'N/A'}\n`;
    response += `• Distance: ${load.total_miles || 0} miles\n\n`;
    response += `FINANCIAL:\n`;
    response += `• Total Rate: $${load.total_rate || 0}\n`;
    response += `• Rate per Mile: $${load.total_miles > 0 ? (load.total_rate / load.total_miles).toFixed(2) : '0.00'}\n\n`;
    response += `ASSIGNMENT:\n`;
    response += `• Driver: ${load.drivers ? `${load.drivers.first_name} ${load.drivers.last_name}` : 'Unassigned'}\n`;
    response += `• Phone: ${load.drivers?.phone || 'N/A'}\n`;
    response += `• Truck: ${load.trucks?.unit_number || 'Unassigned'}\n`;
    response += `• Trailer: ${load.trailers?.unit_number || 'Unassigned'}\n\n`;
    response += `CUSTOMER:\n`;
    response += `• Customer: ${load.customers?.name || 'N/A'}\n`;
    response += `• Broker: ${load.brokers?.name || 'Direct'}\n\n`;

    if (stops && stops.length > 0) {
      response += `STOPS (${stops.length}):\n`;
      stops.forEach((stop, idx) => {
        response += `${idx + 1}. ${stop.stop_type}: ${stop.address}\n`;
        response += `   Scheduled: ${stop.scheduled_time ? new Date(stop.scheduled_time).toLocaleString() : 'N/A'}\n\n`;
      });
    }

    if (load.notes) {
      response += `NOTES:\n${load.notes}\n`;
    }

    return response;
  } catch (error) {
    console.error('Error querying load details:', error);
    return 'Error retrieving load information.';
  }
}

/**
 * Query detailed truck information
 */
async function queryTruckDetails(truckIdentifier: string): Promise<string> {
  try {
    let truck;
    
    // Check if it's a UUID
    if (truckIdentifier.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      const { data } = await supabase
        .from('trucks')
        .select('*')
        .eq('id', truckIdentifier)
        .single();
      truck = data;
    } else {
      // Search by unit number
      const { data } = await supabase
        .from('trucks')
        .select('*')
        .ilike('unit_number', `%${truckIdentifier}%`)
        .limit(1)
        .single();
      truck = data;
    }

    if (!truck) {
      return `Truck "${truckIdentifier}" not found.`;
    }

    // Get assigned driver from equipment_assignments
    const { data: assignment } = await supabase
      .from('equipment_assignments')
      .select('driver_id, drivers(first_name, last_name, phone)')
      .eq('truck_id', truck.id)
      .eq('is_current', true)
      .single();
    
    const driver = assignment?.drivers as any;

    // Get recent maintenance
    const { data: maintenance } = await supabase
      .from('maintenance_logs')
      .select('maintenance_type, service_date, cost, service_provider')
      .eq('truck_id', truck.id)
      .order('service_date', { ascending: false })
      .limit(5);

    // Get revenue stats
    const { data: revenueStats } = await supabase
      .from('revenue_records')
      .select('total_revenue, total_miles')
      .eq('truck_id', truck.id)
      .gte('completed_date', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

    const totalRevenue = revenueStats?.reduce((sum, r) => sum + Number(r.total_revenue || 0), 0) || 0;
    const totalMiles = revenueStats?.reduce((sum, r) => sum + (r.total_miles || 0), 0) || 0;
    const maintenanceCost = maintenance?.reduce((sum, m) => sum + Number(m.cost || 0), 0) || 0;

    let response = `🚛 TRUCK DETAILS: ${truck.unit_number}\n\n`;
    response += `SPECIFICATIONS:\n`;
    response += `• Make: ${truck.make}\n`;
    response += `• Model: ${truck.model}\n`;
    response += `• Year: ${truck.year}\n`;
    response += `• VIN: ${truck.vin || 'N/A'}\n`;
    response += `• Status: ${truck.status}\n`;
    response += `• Mileage: ${truck.mileage?.toLocaleString() || 'N/A'} miles\n\n`;
    response += `ASSIGNMENT:\n`;
    response += `• Driver: ${driver ? `${driver.first_name} ${driver.last_name}` : 'Unassigned'}\n`;
    response += `• Phone: ${driver?.phone || 'N/A'}\n\n`;
    response += `PERFORMANCE (Last 30 Days):\n`;
    response += `• Loads: ${revenueStats?.length || 0}\n`;
    response += `• Revenue: $${totalRevenue.toFixed(2)}\n`;
    response += `• Miles: ${totalMiles.toLocaleString()}\n`;
    response += `• Maintenance Cost: $${maintenanceCost.toFixed(2)}\n\n`;

    if (maintenance && maintenance.length > 0) {
      response += `RECENT MAINTENANCE:\n`;
      maintenance.forEach((m, idx) => {
        response += `${idx + 1}. ${m.maintenance_type} - ${new Date(m.service_date).toLocaleDateString()}\n`;
        response += `   Cost: $${m.cost || 0} - ${m.service_provider || 'N/A'}\n\n`;
      });
    }

    return response;
  } catch (error) {
    console.error('Error querying truck details:', error);
    return 'Error retrieving truck information.';
  }
}

/**
 * Query detailed trailer information
 */
async function queryTrailerDetails(trailerIdentifier: string): Promise<string> {
  try {
    let trailer;
    
    // Check if it's a UUID
    if (trailerIdentifier.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      const { data } = await supabase
        .from('trailers')
        .select('*')
        .eq('id', trailerIdentifier)
        .single();
      trailer = data;
    } else {
      // Search by unit number
      const { data } = await supabase
        .from('trailers')
        .select('*')
        .ilike('unit_number', `%${trailerIdentifier}%`)
        .limit(1)
        .single();
      trailer = data;
    }

    if (!trailer) {
      return `Trailer "${trailerIdentifier}" not found.`;
    }

    // Get assigned driver
    const { data: driver } = await supabase
      .from('drivers')
      .select('first_name, last_name, phone')
      .eq('assigned_trailer_id', trailer.id)
      .single();

    // Get recent maintenance
    const { data: maintenance } = await supabase
      .from('maintenance_logs')
      .select('maintenance_type, service_date, cost, service_provider')
      .eq('trailer_id', trailer.id)
      .order('service_date', { ascending: false })
      .limit(5);

    const maintenanceCost = maintenance?.reduce((sum, m) => sum + Number(m.cost || 0), 0) || 0;

    let response = `🚚 TRAILER DETAILS: ${trailer.unit_number}\n\n`;
    response += `SPECIFICATIONS:\n`;
    response += `• Make: ${trailer.make}\n`;
    response += `• Model: ${trailer.model}\n`;
    response += `• Year: ${trailer.year}\n`;
    response += `• VIN: ${trailer.vin || 'N/A'}\n`;
    response += `• Type: ${trailer.trailer_type || 'N/A'}\n`;
    response += `• Status: ${trailer.status}\n\n`;
    response += `ASSIGNMENT:\n`;
    response += `• Driver: ${driver ? `${driver.first_name} ${driver.last_name}` : 'Unassigned'}\n`;
    response += `• Phone: ${driver?.phone || 'N/A'}\n\n`;
    response += `MAINTENANCE:\n`;
    response += `• Total Cost: $${maintenanceCost.toFixed(2)}\n`;
    response += `• Service Records: ${maintenance?.length || 0}\n\n`;

    if (maintenance && maintenance.length > 0) {
      response += `RECENT MAINTENANCE:\n`;
      maintenance.forEach((m, idx) => {
        response += `${idx + 1}. ${m.maintenance_type} - ${new Date(m.service_date).toLocaleDateString()}\n`;
        response += `   Cost: $${m.cost || 0} - ${m.service_provider || 'N/A'}\n\n`;
      });
    }

    return response;
  } catch (error) {
    console.error('Error querying trailer details:', error);
    return 'Error retrieving trailer details.';
  }
}

/**
 * Process user message and route to appropriate query function
 * ULTRA-ADVANCED QUERY ROUTING - Handles all business owner questions
 */
async function processUserQuery(userMessage: string): Promise<string> {
  const message = userMessage.toLowerCase();

  // ============================================
  // QUICK ACTIONS - Priority routing for UI buttons
  // ============================================
  
  // Weekly Revenue Quick Action
  if (message.includes('what is our revenue for this week') || 
      (message.includes('revenue') && message.includes('this week'))) {
    return await queryWeeklyRevenue();
  }

  // Top Drivers Quick Action
  if (message.includes('show me top 5 drivers by revenue this week') ||
      (message.includes('top') && message.includes('drivers') && message.includes('revenue'))) {
    const { queryTopDriversByRevenue } = await import('./topDriversQuery');
    return await queryTopDriversByRevenue(5, 'week');
  }

  // Top Customers Quick Action
  if (message.includes('who are our top customers by revenue') ||
      (message.includes('top') && message.includes('customers'))) {
    return await queryTopCustomers(10, 'month');
  }

  // Idle Trucks Quick Action
  if (message.includes('which trucks are sitting idle') ||
      (message.includes('idle') && message.includes('truck'))) {
    return await queryTruckUtilization('month');
  }

  // Maintenance Costs Quick Action
  if (message.includes('show me maintenance costs this month') ||
      (message.includes('maintenance') && message.includes('cost') && !message.includes('truck'))) {
    return await queryFleetMaintenanceCosts();
  }

  // Dispatcher Performance Quick Action
  if (message.includes('show me dispatcher performance this week') ||
      (message.includes('dispatcher') && message.includes('performance'))) {
    const { queryDispatcherPerformance } = await import('./dispatcherPerformanceQuery');
    const timeframe = message.includes('week') ? 'week' : 
                     message.includes('month') ? 'month' : 
                     message.includes('year') ? 'year' : 'week';
    return await queryDispatcherPerformance(timeframe);
  }

  // ============================================
  // REVENUE & FINANCIAL QUERIES
  // ============================================
  
  // Revenue by timeframe
  if (message.includes('revenue') || message.includes('income') || message.includes('earnings')) {
    if (message.includes('week') || message.includes('weekly')) {
      return await queryWeeklyRevenue();
    }
    if (message.includes('month') || message.includes('monthly')) {
      return await queryWeeklyRevenue(); // Will adapt to show monthly
    }
    if (message.includes('year') || message.includes('yearly') || message.includes('annual')) {
      return await queryWeeklyRevenue(); // Will adapt to show yearly
    }
  }

  // ============================================
  // DRIVER QUERIES
  // ============================================
  
  // Top drivers
  if ((message.includes('top') || message.includes('best')) && message.includes('driver')) {
    const { queryTopDriversByRevenue } = await import('./topDriversQuery');
    const limit = message.match(/top\s+(\d+)/)?.[1] || '5';
    const timeframe = message.includes('week') ? 'week' : 
                     message.includes('month') ? 'month' : 
                     message.includes('year') ? 'year' : 'week';
    return await queryTopDriversByRevenue(parseInt(limit), timeframe);
  }

  // Driver details queries (info about, tell me about, show me details)
  if ((message.includes('info') || message.includes('details') || message.includes('tell me about') || message.includes('show me')) && 
      message.includes('driver')) {
    const nameMatch = userMessage.match(/driver\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i) || 
                     userMessage.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (nameMatch) {
      return await queryDriverDetails(nameMatch[1]);
    }
  }

  // Load details queries
  if ((message.includes('info') || message.includes('details') || message.includes('tell me about') || message.includes('show me')) && 
      message.includes('load')) {
    const loadMatch = userMessage.match(/load\s+([A-Z0-9-]+)/i) || 
                     userMessage.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (loadMatch) {
      return await queryLoadDetails(loadMatch[1]);
    }
  }

  // Truck details queries
  if ((message.includes('info') || message.includes('details') || message.includes('tell me about') || message.includes('show me')) && 
      message.includes('truck')) {
    const truckMatch = userMessage.match(/truck\s+([A-Z0-9]+)/i) || 
                      userMessage.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (truckMatch) {
      return await queryTruckDetails(truckMatch[1]);
    }
  }

  // Trailer details queries
  if ((message.includes('info') || message.includes('details') || message.includes('tell me about') || message.includes('show me')) && 
      message.includes('trailer')) {
    const trailerMatch = userMessage.match(/trailer\s+([A-Z0-9]+)/i) || 
                        userMessage.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    if (trailerMatch) {
      return await queryTrailerDetails(trailerMatch[1]);
    }
  }

  // Driver earnings queries
  if ((message.includes('how much') || message.includes('earnings') || message.includes('made') || message.includes('revenue')) && 
      (message.includes('driver') || message.match(/[A-Z][a-z]+\s+[A-Z][a-z]+/))) {
    const nameMatch = message.match(/(?:driver\s+)?([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
    if (nameMatch) {
      const timeframe = message.includes('week') ? 'week' : message.includes('month') ? 'month' : 'month';
      return await queryDriverEarnings(nameMatch[1], timeframe);
    }
  }

  // Dispatcher driver queries (what drivers, how many drivers, list drivers)
  if ((message.includes('driver') || message.includes('drivers')) && 
      (message.includes('assigned') || message.includes('have') || message.includes('has') || 
       message.includes('what') || message.includes('list') || message.includes('show') ||
       message.includes('how many'))) {
    // Extract dispatcher name
    const nameMatch = message.match(/drivers?\s+(?:are\s+)?(?:assigned\s+to\s+)?([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i) ||
                     message.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\s+(?:have|has)/i) ||
                     message.match(/(?:to|for)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
    if (nameMatch) {
      return await queryDispatcherDrivers(nameMatch[1]);
    }
  }

  // Dispatcher trucks WITH drivers (who drives [dispatcher]'s trucks, show trucks and drivers)
  if ((message.includes('truck') || message.includes('trucks')) && 
      (message.includes('driver') || message.includes('drivers') || message.includes('drive') || message.includes('driving'))) {
    // Extract dispatcher name
    const nameMatch = message.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)'?s?\s+trucks?/i) ||
                     message.match(/trucks?\s+(?:and\s+drivers?\s+)?(?:for|of)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
    if (nameMatch) {
      return await queryDispatcherTrucksWithDrivers(nameMatch[1]);
    }
  }

  // Dispatcher truck count queries (how many trucks, what trucks, list trucks)
  if ((message.includes('truck') || message.includes('trucks')) && 
      (message.includes('how many') || message.includes('what') || message.includes('list') || 
       message.includes('show') || message.includes('have') || message.includes('has')) &&
      !message.includes('driver')) {
    // Extract dispatcher name - multiple patterns
    // Matches: "how many trucks Ned have", "what trucks have Alen", "what trucks does Mark have"
    const nameMatch = message.match(/trucks?\s+(?:does\s+)?([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\s+(?:have|has)/i) ||
                     message.match(/trucks?\s+(?:have|has)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i) ||
                     message.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\s+(?:have|has)\s+\d+\s+trucks?/i) ||
                     message.match(/(?:for|to)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
    if (nameMatch) {
      return await queryDispatcherTrucks(nameMatch[1]);
    }
  }

  // Truck driver assignment queries (who is driving, who has truck, driver for truck)
  // Also detect Serbian/Croatian: "ko vozi", "vozač", "tko vozi"
  if (((message.includes('who') || message.includes('driver') || message.includes('ko ') || message.includes('tko ') || message.includes('vozač') || message.includes('vozac')) && 
      (message.includes('drive') || message.includes('driving') || message.includes('assigned') || message.includes('has') || message.includes('vozi'))) ||
      (message.match(/\d{4}/) && (message.includes('vozi') || message.includes('vozač') || message.includes('vozac')))) {
    const truckMatch = message.match(/(\d{4})/);
    if (truckMatch) {
      return await queryTruckDriver(truckMatch[1]);
    }
  }

  // Truck mileage queries
  if ((message.includes('miles') || message.includes('mileage') || message.includes('drove')) && 
      (message.includes('truck') || message.match(/truck[\s#-]*\w+/i))) {
    const truckMatch = message.match(/truck[\s#-]*(\w+)/i);
    if (truckMatch) {
      const timeframe = message.includes('week') ? 'week' : message.includes('month') ? 'month' : 'month';
      return await queryTruckMileage(truckMatch[1], timeframe);
    }
  }

  // Weekly/monthly revenue queries
  if ((message.includes('revenue') || message.includes('income') || message.includes('earnings')) && 
      (message.includes('week') || message.includes('this week') || message.includes('weekly'))) {
    return await queryWeeklyRevenue();
  }

  // Truck maintenance cost queries with specific truck
  if (message.includes('maintenance') && message.includes('cost') && 
      (message.includes('truck') || message.match(/truck[\s#-]*\w+/i))) {
    const truckMatch = message.match(/truck[\s#-]*(\w+)/i);
    if (truckMatch) {
      const timeframe = message.includes('week') ? 'week' : 
                       message.includes('month') ? 'month' : 
                       message.includes('year') ? 'year' : 'all';
      return await queryTruckMaintenanceCosts(truckMatch[1], timeframe);
    }
  }

  // Truck maintenance queries
  if (message.includes('maintenance') && (message.includes('truck') || message.match(/truck[\s#-]*\d+/))) {
    const truckMatch = message.match(/truck[\s#-]*(\w+)/i);
    if (truckMatch) {
      const truckIdentifier = truckMatch[1];
      // Try to find truck by number
      const { data } = await supabase
        .from('trucks')
        .select('id')
        .ilike('truck_number', `%${truckIdentifier}%`)
        .limit(1)
        .single();
      
      if (data) {
        return await queryTruckMaintenance(data.id);
      }
    }
  }

  // Driver location queries
  if (message.includes('where') || message.includes('location')) {
    // Check if asking about a driver (not a truck)
    if (!message.includes('truck')) {
      // Extract potential driver name - look for capitalized words or names
      const nameMatch = message.match(/(?:where is|location of|find)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i);
      if (nameMatch) {
        return await queryDriverLocation(nameMatch[1]);
      }
      // Also try to match just a name anywhere in the message
      const simpleNameMatch = message.match(/([A-Z][a-z]+\s+[A-Z][a-z]+)/);
      if (simpleNameMatch) {
        return await queryDriverLocation(simpleNameMatch[1]);
      }
    }
  }

  // Truck location queries
  if ((message.includes('location') || message.includes('where is')) && message.includes('truck')) {
    const truckMatch = message.match(/truck[\s#-]*(\w+)/i);
    if (truckMatch) {
      return await queryTruckLocation(truckMatch[1]);
    }
  }

  // Top customers queries
  if ((message.includes('top') || message.includes('best')) && (message.includes('customer') || message.includes('client'))) {
    const timeframe = message.includes('week') ? 'week' : message.includes('month') ? 'month' : 'month';
    return await queryTopCustomers(10, timeframe);
  }

  // Route/lane analytics
  if ((message.includes('route') || message.includes('lane')) && 
      (message.includes('profitable') || message.includes('revenue') || message.includes('best'))) {
    const timeframe = message.includes('week') ? 'week' : message.includes('month') ? 'month' : 'month';
    return await queryRouteAnalytics(timeframe);
  }

  // Company trucks queries
  if ((message.includes('how many') || message.includes('count') || message.includes('number of')) && 
      message.includes('truck') && 
      (message.includes('apollo') || message.includes('tesla') || message.includes('t mark') || message.includes('tmark') || message.includes('company'))) {
    const companyMatch = message.match(/(apollo|tesla|t[\s-]?mark)/i);
    if (companyMatch) {
      let companyName = companyMatch[1];
      if (companyName.toLowerCase().includes('mark')) companyName = 'T Mark';
      return await queryTrucksByCompany(companyName);
    }
  }

  // All trucks by company
  if (message.includes('truck') && message.includes('company') && !message.includes('how many')) {
    return await queryTrucksByCompany();
  }

  // Truck utilization and idle trucks
  if ((message.includes('utilization') || message.includes('idle') || message.includes('sitting')) && message.includes('truck')) {
    const timeframe = message.includes('week') ? 'week' : 'month';
    return await queryTruckUtilization(timeframe);
  }

  // Driver performance metrics
  if ((message.includes('driver') && message.includes('performance')) || 
      (message.includes('top') && message.includes('driver'))) {
    const timeframe = message.includes('week') ? 'week' : 'month';
    return await queryDriverPerformance(timeframe);
  }

  // Dispatcher performance
  if (message.includes('dispatcher') && (message.includes('performance') || message.includes('show'))) {
    const { queryDispatcherPerformance } = await import('./dispatcherPerformanceQuery');
    const timeframe = message.includes('week') ? 'week' : 
                     message.includes('month') ? 'month' : 
                     message.includes('year') ? 'year' : 'week';
    return await queryDispatcherPerformance(timeframe);
  }

  // Predictive maintenance
  if ((message.includes('due') || message.includes('need') || message.includes('predict')) && message.includes('maintenance')) {
    return await queryPredictiveMaintenance();
  }

  // Real-time operations dashboard
  if ((message.includes('real') && message.includes('time')) || 
      (message.includes('active') && (message.includes('load') || message.includes('truck'))) ||
      (message.includes('right now') || message.includes('currently'))) {
    return await queryRealTimeOperations();
  }

  // Financial analysis
  if ((message.includes('profit') || message.includes('margin') || message.includes('financial')) && 
      (message.includes('analysis') || message.includes('breakdown'))) {
    const timeframe = message.includes('week') ? 'week' : 'month';
    return await queryFinancialAnalysis(timeframe);
  }

  // Compliance alerts
  if ((message.includes('compliance') || message.includes('expir') || message.includes('document')) && 
      (message.includes('alert') || message.includes('due') || message.includes('renew'))) {
    return await queryComplianceAlerts();
  }

  // Trucks with most issues
  if (message.includes('most') && (message.includes('issue') || message.includes('maintenance') || message.includes('problem'))) {
    return await queryTrucksWithMostIssues();
  }

  // Driver load statistics
  if (message.includes('driver') && (message.includes('load') || message.includes('most'))) {
    const timeframe = message.includes('week') ? 'week' : 'month';
    return await queryDriverLoadStats(timeframe);
  }

  // List all trucks
  if ((message.includes('list') || message.includes('show')) && message.includes('truck') && !message.includes('maintenance')) {
    return await queryAllTrucks();
  }

  // Fleet maintenance costs / parts spending
  if ((message.includes('total') || message.includes('how much') || message.includes('spent')) && 
      (message.includes('maintenance') || message.includes('parts') || message.includes('cost'))) {
    return await queryFleetMaintenanceCosts();
  }

  // Maintenance summary
  if (message.includes('maintenance') && (message.includes('summary') || message.includes('overview') || message.includes('all'))) {
    return await queryFleetMaintenanceCosts();
  }

  // Safety incidents and accidents
  if (message.includes('safety') || message.includes('accident') || message.includes('incident')) {
    const timeframe = message.includes('week') ? 'week' : message.includes('year') ? 'year' : 'month';
    return await querySafetyIncidents(timeframe);
  }

  // Accounting summary
  if ((message.includes('accounting') || message.includes('invoice') || message.includes('expense') || message.includes('profit')) && 
      (message.includes('summary') || message.includes('overview') || message.includes('total'))) {
    const timeframe = message.includes('week') ? 'week' : 'month';
    return await queryAccountingSummary(timeframe);
  }

  // Samsara locations / vehicle tracking
  if ((message.includes('location') || message.includes('where') || message.includes('gps') || message.includes('tracking')) && 
      (message.includes('all') || message.includes('vehicle') || message.includes('fleet'))) {
    return await querySamsaraLocations();
  }

  // Documents query
  if (message.includes('document') && (message.includes('show') || message.includes('list') || message.includes('find'))) {
    // Try to extract category from message
    const categories = ['bol', 'pod', 'invoice', 'insurance', 'registration', 'permit', 'license', 'medical'];
    const category = categories.find(cat => message.includes(cat));
    return await queryDocuments(category);
  }

  // User activity and system usage
  if ((message.includes('user') || message.includes('activity') || message.includes('login')) && 
      (message.includes('who') || message.includes('recent') || message.includes('active'))) {
    return await queryUserActivity();
  }

  return ''; // No specialized handler matched - return empty string to indicate no match
}

/**
 * Send a chat message to OpenAI and get a response
 */
export async function sendChatMessage(
  userMessage: string,
  conversationHistory: ChatMessage[] = []
): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const config = await getOpenAIConfig();

    if (!config.enabled) {
      return {
        success: false,
        error: 'ChatGPT AI Assistant is not enabled. Please enable it in Settings → Integrations.',
      };
    }

    // NOTE: The API key is no longer read in the browser — it lives server-side
    // and is injected by the chat-proxy edge function. The proxy enforces the
    // "API key is not configured" check and returns the same error message, so
    // behavior is preserved without exposing the secret to the client.

    // Check if this is a specialized query that can be handled directly
    const specializedResponse = await processUserQuery(userMessage);

    if (specializedResponse) {
      // We have direct data, send it to AI for natural language formatting
      const fleetContext = await getFleetContext();
      const messages: ChatMessage[] = [
        {
          role: 'system',
          content: fleetContext + '\n\nIMPORTANT: Format the following data in plain text without any markdown (no **, *, _, #). Be brief and direct. No pleasantries.',
        },
        {
          role: 'user',
          content: `User asked: "${userMessage}"\n\nData retrieved:\n${specializedResponse}`,
        },
      ];

      // Route the privileged OpenAI call through the chat-proxy edge function.
      // The proxy reads the API key server-side and injects Authorization;
      // supabase auto-attaches the caller's auth header.
      const { data, error } = await supabase.functions.invoke('chat-proxy', {
        body: {
          model: config.model,
          messages: messages,
          temperature: 0.7,
          max_tokens: 500,
        },
      });

      if (error) {
        throw new Error(data?.error || error.message || 'Failed to reach AI service');
      }
      if (data?.error) {
        throw new Error(data.error);
      }

      const assistantMessage = data?.choices?.[0]?.message?.content;

      if (!assistantMessage) {
        throw new Error('No response from OpenAI');
      }

      return {
        success: true,
        message: assistantMessage,
      };
    }

    // Regular conversational query
    const fleetContext = await getFleetContext();

    // Build messages array
    const messages: ChatMessage[] = [
      {
        role: 'system',
        content: fleetContext,
      },
      ...conversationHistory,
      {
        role: 'user',
        content: userMessage,
      },
    ];

    // Call OpenAI API via the chat-proxy edge function (API key stays server-side).
    const { data, error } = await supabase.functions.invoke('chat-proxy', {
      body: {
        model: config.model,
        messages: messages,
        temperature: 0.8,
        max_tokens: 600,
      },
    });

    if (error) {
      throw new Error(data?.error || error.message || 'Failed to reach AI service');
    }
    if (data?.error) {
      throw new Error(data.error);
    }

    const assistantMessage = data?.choices?.[0]?.message?.content;

    if (!assistantMessage) {
      throw new Error('No response from OpenAI');
    }

    return {
      success: true,
      message: assistantMessage,
    };
  } catch (error) {
    console.error('Error sending chat message:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send message',
    };
  }
}

/**
 * Query specific truck maintenance data with full cost breakdown
 */
export async function queryTruckMaintenance(truckId: string): Promise<string> {
  try {
    const { data, error } = await supabase
      .from('maintenance_logs')
      .select(`
        id,
        log_number,
        maintenance_type,
        description,
        cost,
        labor_cost,
        parts_cost,
        company_cost_amount,
        service_date,
        odometer,
        service_provider,
        trucks (truck_number, make, model)
      `)
      .eq('truck_id', truckId)
      .order('service_date', { ascending: false })
      .limit(20);

    if (error) throw error;

    if (!data || data.length === 0) {
      return `No maintenance records found for truck ${truckId}.`;
    }

    const truckInfo = Array.isArray(data[0].trucks) ? data[0].trucks[0] : data[0].trucks;
    
    // Calculate totals
    const totalCost = data.reduce((sum, log) => sum + (Number(log.cost) || 0), 0);
    const totalLabor = data.reduce((sum, log) => sum + (Number(log.labor_cost) || 0), 0);
    const totalParts = data.reduce((sum, log) => sum + (Number(log.parts_cost) || 0), 0);
    
    const summary = `Found ${data.length} maintenance records for truck ${truckInfo?.truck_number}:\n\n` +
      `TOTAL COSTS:\n` +
      `- Total Spent: $${totalCost.toFixed(2)}\n` +
      `- Labor Costs: $${totalLabor.toFixed(2)}\n` +
      `- Parts Costs: $${totalParts.toFixed(2)}\n\n` +
      `RECENT MAINTENANCE:\n` +
      data.slice(0, 10).map((log, idx) => 
        `${idx + 1}. ${log.maintenance_type} (${log.log_number})\n` +
        `   Date: ${new Date(log.service_date).toLocaleDateString()}\n` +
        `   Provider: ${log.service_provider || 'N/A'}\n` +
        `   Total Cost: $${Number(log.cost || 0).toFixed(2)}\n` +
        `   - Labor: $${Number(log.labor_cost || 0).toFixed(2)}\n` +
        `   - Parts: $${Number(log.parts_cost || 0).toFixed(2)}\n` +
        `   Odometer: ${log.odometer?.toLocaleString() || 'N/A'} miles\n` +
        `   Description: ${log.description || 'N/A'}`
      ).join('\n\n');

    return summary;
  } catch (error) {
    console.error('Error querying truck maintenance:', error);
    return 'Error retrieving maintenance data.';
  }
}

/**
 * Query safety incidents and accidents
 */
async function querySafetyIncidents(timeframe: 'week' | 'month' | 'year' = 'month'): Promise<string> {
  try {
    const daysAgo = timeframe === 'week' ? 7 : timeframe === 'month' ? 30 : 365;
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysAgo);

    const { data: accidents, error } = await supabase
      .from('accidents')
      .select(`
        id,
        accident_date,
        severity,
        driver_id,
        truck_id,
        description,
        location,
        drivers (first_name, last_name),
        trucks (truck_number)
      `)
      .gte('accident_date', startDate.toISOString())
      .order('accident_date', { ascending: false });

    if (error) throw error;

    if (!accidents || accidents.length === 0) {
      return `No safety incidents found in the last ${timeframe}.`;
    }

    const summary = `SAFETY INCIDENTS (Last ${timeframe}):\n\n` +
      `Total Incidents: ${accidents.length}\n\n` +
      accidents.map((acc, idx) => {
        const driver = Array.isArray(acc.drivers) ? acc.drivers[0] : acc.drivers;
        const truck = Array.isArray(acc.trucks) ? acc.trucks[0] : acc.trucks;
        return `${idx + 1}. ${new Date(acc.accident_date).toLocaleDateString()}\n` +
          `   Driver: ${driver ? `${driver.first_name} ${driver.last_name}` : 'N/A'}\n` +
          `   Truck: ${truck?.truck_number || 'N/A'}\n` +
          `   Severity: ${acc.severity || 'N/A'}\n` +
          `   Location: ${acc.location || 'N/A'}\n` +
          `   Description: ${acc.description || 'N/A'}`;
      }).join('\n\n');

    return summary;
  } catch (error) {
    console.error('Error querying safety incidents:', error);
    return 'Error retrieving safety incident data.';
  }
}

/**
 * Query accounting summary - invoices, expenses, settlements
 */
async function queryAccountingSummary(timeframe: 'week' | 'month' = 'month'): Promise<string> {
  try {
    const daysAgo = timeframe === 'week' ? 7 : 30;
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysAgo);

    const [invoices, expenses, settlements] = await Promise.all([
      supabase
        .from('invoices')
        .select('total_amount, status, created_at')
        .gte('created_at', startDate.toISOString()),
      supabase
        .from('expenses')
        .select('amount, category, expense_date')
        .gte('expense_date', startDate.toISOString()),
      supabase
        .from('settlements')
        .select('total_amount, status, settlement_date')
        .gte('settlement_date', startDate.toISOString())
    ]);

    const totalInvoiced = invoices.data?.reduce((sum, inv) => sum + Number(inv.total_amount || 0), 0) || 0;
    const paidInvoices = invoices.data?.filter(inv => inv.status === 'paid').length || 0;
    const pendingInvoices = invoices.data?.filter(inv => inv.status === 'pending').length || 0;
    
    const totalExpenses = expenses.data?.reduce((sum, exp) => sum + Number(exp.amount || 0), 0) || 0;
    const totalSettlements = settlements.data?.reduce((sum, set) => sum + Number(set.total_amount || 0), 0) || 0;

    const netProfit = totalInvoiced - totalExpenses - totalSettlements;

    const summary = `ACCOUNTING SUMMARY (Last ${timeframe}):\n\n` +
      `REVENUE:\n` +
      `- Total Invoiced: $${totalInvoiced.toFixed(2)}\n` +
      `- Paid Invoices: ${paidInvoices}\n` +
      `- Pending Invoices: ${pendingInvoices}\n\n` +
      `EXPENSES:\n` +
      `- Total Expenses: $${totalExpenses.toFixed(2)}\n` +
      `- Driver Settlements: $${totalSettlements.toFixed(2)}\n\n` +
      `NET PROFIT: $${netProfit.toFixed(2)}`;

    return summary;
  } catch (error) {
    console.error('Error querying accounting summary:', error);
    return 'Error retrieving accounting data.';
  }
}

/**
 * Query all Samsara vehicle locations with speed
 */
async function querySamsaraLocations(): Promise<string> {
  try {
    const { data: locations, error } = await supabase
      .from('vehicle_locations')
      .select(`
        samsara_vehicle_id,
        latitude,
        longitude,
        speed_mph,
        heading,
        recorded_at,
        trucks (truck_number, unit_number)
      `)
      .order('recorded_at', { ascending: false })
      .limit(50);

    if (error) throw error;

    if (!locations || locations.length === 0) {
      return 'No recent vehicle location data available. Locations are updated via Samsara webhook.';
    }

    // Group by vehicle to get latest location per vehicle
    const latestByVehicle = new Map();
    locations.forEach(loc => {
      if (!latestByVehicle.has(loc.samsara_vehicle_id)) {
        latestByVehicle.set(loc.samsara_vehicle_id, loc);
      }
    });

    const summary = `VEHICLE LOCATIONS (Latest):\n\n` +
      Array.from(latestByVehicle.values()).map((loc, idx) => {
        const truck = Array.isArray(loc.trucks) ? loc.trucks[0] : loc.trucks;
        const minutesAgo = Math.floor((Date.now() - new Date(loc.recorded_at).getTime()) / 60000);
        return `${idx + 1}. ${truck?.truck_number || truck?.unit_number || loc.samsara_vehicle_id}\n` +
          `   Speed: ${loc.speed_mph || 0} mph\n` +
          `   Location: ${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}\n` +
          `   Heading: ${loc.heading || 'N/A'}°\n` +
          `   Updated: ${minutesAgo} minutes ago`;
      }).join('\n\n');

    return summary;
  } catch (error) {
    console.error('Error querying Samsara locations:', error);
    return 'Error retrieving vehicle location data.';
  }
}

/**
 * Query documents by type or category
 */
async function queryDocuments(category?: string): Promise<string> {
  try {
    let query = supabase
      .from('documents')
      .select('id, document_name, document_type, category, uploaded_at, file_size')
      .order('uploaded_at', { ascending: false })
      .limit(20);

    if (category) {
      query = query.ilike('category', `%${category}%`);
    }

    const { data, error } = await query;

    if (error) throw error;

    if (!data || data.length === 0) {
      return category 
        ? `No documents found in category "${category}".`
        : 'No documents found in the system.';
    }

    const summary = category
      ? `DOCUMENTS - ${category.toUpperCase()} (${data.length} found):\n\n`
      : `RECENT DOCUMENTS (${data.length} found):\n\n`;

    return summary + data.map((doc, idx) => 
      `${idx + 1}. ${doc.document_name}\n` +
      `   Type: ${doc.document_type || 'N/A'}\n` +
      `   Category: ${doc.category || 'N/A'}\n` +
      `   Size: ${(doc.file_size / 1024).toFixed(2)} KB\n` +
      `   Uploaded: ${new Date(doc.uploaded_at).toLocaleDateString()}`
    ).join('\n\n');
  } catch (error) {
    console.error('Error querying documents:', error);
    return 'Error retrieving document data.';
  }
}

/**
 * Query user activity and system usage
 */
async function queryUserActivity(): Promise<string> {
  try {
    const { data: users, error: usersError } = await supabase
      .from('users')
      .select('id, email, role, is_active, last_sign_in_at')
      .order('last_sign_in_at', { ascending: false })
      .limit(10);

    if (usersError) throw usersError;

    const { data: activity, error: activityError } = await supabase
      .from('activity_log')
      .select('action, created_at, users(email)')
      .order('created_at', { ascending: false })
      .limit(10);

    if (activityError) throw activityError;

    const activeUsers = users?.filter(u => u.is_active).length || 0;
    const totalUsers = users?.length || 0;

    let summary = `USER ACTIVITY:\n\n`;
    summary += `Total Users: ${totalUsers}\n`;
    summary += `Active Users: ${activeUsers}\n\n`;
    
    if (users && users.length > 0) {
      summary += `RECENT USER LOGINS:\n`;
      summary += users.slice(0, 5).map((user, idx) => 
        `${idx + 1}. ${user.email} (${user.role})\n` +
        `   Last Login: ${user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : 'Never'}\n` +
        `   Status: ${user.is_active ? 'Active' : 'Inactive'}`
      ).join('\n\n');
    }

    if (activity && activity.length > 0) {
      summary += `\n\nRECENT SYSTEM ACTIVITY:\n`;
      summary += activity.slice(0, 5).map((act, idx) => {
        const user = Array.isArray(act.users) ? act.users[0] : act.users;
        return `${idx + 1}. ${act.action}\n` +
          `   User: ${user?.email || 'System'}\n` +
          `   Time: ${new Date(act.created_at).toLocaleString()}`;
      }).join('\n\n');
    }

    return summary;
  } catch (error) {
    console.error('Error querying user activity:', error);
    return 'Error retrieving user activity data.';
  }
}

/**
 * Query truck location using Samsara API
 */
export async function queryTruckLocation(truckIdentifier: string): Promise<string> {
  try {
    // Check if Samsara is enabled (via the status-only edge function).
    const { data: statusData } = await supabase.functions.invoke('integration-status');
    if (statusData?.status?.samsara?.enabled !== true) {
      return 'Samsara GPS integration is not enabled. Please enable it in Settings → Integrations to track truck locations.';
    }

    // Import location service to get vehicle locations
    const { getVehicleLocations } = await import('./locationService');
    
    const result = await getVehicleLocations();
    
    if (result.success && result.locations) {
      // Find the specific vehicle by ID or name
      const location = result.locations.find(loc => 
        loc.vehicleId.toLowerCase().includes(truckIdentifier.toLowerCase())
      );
      
      if (location) {
        return `Truck ${truckIdentifier} is currently located at:\n` +
          `Latitude: ${location.latitude}\n` +
          `Longitude: ${location.longitude}\n` +
          `Speed: ${location.speed || 0} mph\n` +
          `Last updated: ${new Date(location.time).toLocaleString()}`;
      } else {
        return `Unable to find location for truck ${truckIdentifier}. Available vehicles: ${result.locations.length}. Please verify the truck identifier.`;
      }
    } else {
      return result.error || 'Unable to retrieve vehicle locations from Samsara.';
    }
  } catch (error) {
    console.error('Error querying truck location:', error);
    return 'Error retrieving truck location. Please ensure Samsara integration is properly configured.';
  }
}

export type { ChatMessage, OpenAIConfig };
